import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useParams, useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  getResponseById,
  updatePricingOverrides,
  convertToProgram,
  rejectResponse,
  extendResponseExpiry,
  delegateResponse,
  markAsLost,
  toggleNdaRequired
} from "../../utils/api/endpoints/questionnaire";
import usePageTitle from "../../utils/hooks/usePageTitle";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import {
  Download,
  CheckCircle2,
  Clock,
  RefreshCw,
  X,
  Eye,
  Shield,
  PenLine,
  Loader2,
  Copy,
  Send,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Info,
  ArrowLeft,
  XCircle,
  Lock,
  Edit3,
  Search,
  Sparkles,
  ShieldCheck,
  Layers,
  Sliders,
  AlertTriangle,
  FileText,
  Target,
  Check
} from "lucide-react";
import axios from "../../utils/api/axios";
import ConfirmationModal from "../../components/common/ConfirmationModal";
import { SsCountersignModal } from "../../components/scoping/SsCountersignModal";
import { pdf } from "@react-pdf/renderer";
import { NdaPdfDocument } from "../../components/scoping/NdaPdfDocument";
import { SowBuilderModal } from "../../components/scoping/SowBuilderModal";
import { getSow } from "../../utils/api/endpoints/sow";
import type { SowStatus, SowBlob } from "../../utils/api/endpoints/sow";

const METHODOLOGY_LABELS: Record<string, string> = {
  WEB_APP: "Web Application Pentest",
  API: "API Service (REST/GraphQL/gRPC)",
  MOBILE_APP: "Mobile App (iOS/Android)",
  AI_LLM: "AI & LLM Assessment",
  CODE_REVIEW: "Secure Code Review",
  EXTERNAL_NETWORK: "External Network Pentest",
  INTERNAL_NETWORK: "Internal Active Directory Test",
  VPN_FIREWALL_REVIEW: "VPN & Firewall Config Review",
  CLOUD_CONFIG: "Cloud Config Audit",
  CLOUD_PENTEST: "Cloud Infrastructure Pentest",
  M365_REVIEW: "M365 Tenant Audit"
};

const REGION_DAILY_RATES: Record<string, { currency: string; rate: number }> = {
  NZ: { currency: "NZD", rate: 2000 },
  AU: { currency: "AUD", rate: 2200 },
  US: { currency: "USD", rate: 1800 },
  SG: { currency: "SGD", rate: 2500 }
};

interface PlaybookItem {
  id: string;
  code: string;
  title: string;
  description: string;
  meta: string;
  category: "must" | "strong" | "optional";
  locked?: boolean;
}

const DEFAULT_PLAYBOOK_ITEMS: PlaybookItem[] = [
  {
    id: "methodology",
    code: "METHOD-1PG",
    title: "Methodology one-pager",
    description: "Overview of CTB's testing methodology and approach.",
    meta: "PP-01 · Always included",
    category: "must",
    locked: true
  },
  {
    id: "sow_lite",
    code: "SOW-LITE",
    title: "SOW Lite",
    description: "Lightweight statement of work for early-stage startups.",
    meta: "PP-02 · Startup tier, tight budget band",
    category: "must",
    locked: true
  },
  {
    id: "readiness",
    code: "READINESS-CHK",
    title: "Readiness checklist",
    description: "Pre-engagement readiness checklist for first-time buyers.",
    meta: "PP-02 · Startup tier, first-time buyer",
    category: "must",
    locked: true
  },
  {
    id: "nurture",
    code: "NURTURE-900",
    title: "90-day nurture sequence",
    description: "Automated nurture sequence for the education phase.",
    meta: "PP-18 · Route: education sequence",
    category: "strong"
  },
  {
    id: "case_studies",
    code: "CASE-STUDY",
    title: "Relevant case studies",
    description: "Two startup web-app engagements with comparable scope.",
    meta: "PP-22 · Tier match",
    category: "optional"
  },
  {
    id: "pricing_faq",
    code: "PRICING-FAQ",
    title: "Pricing FAQ",
    description: "Answers the day-rate and scope-change questions early.",
    meta: "PP-24 · No budget band supplied",
    category: "optional"
  }
];

const ScopingSessionDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  usePageTitle("Scoping Session Details");
  const { role: currentUserRole, username: currentUsername } = useUserCredentials();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any | null>(null);
  const [isConverting, setIsConverting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isConfirmRejectOpen, setIsConfirmRejectOpen] = useState(false);

  // Extend Expiry modal state
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isCountersignModalOpen, setIsCountersignModalOpen] = useState(false);
  const [customExpiryDate, setCustomExpiryDate] = useState("");
  const [isExtending, setIsExtending] = useState(false);

  // Lost Modal State
  const [lostModalOpen, setLostModalOpen] = useState(false);
  const [lostReason, setLostReason] = useState("");
  const [lostNote, setLostNote] = useState("");
  const [submittingLost, setSubmittingLost] = useState(false);

  // Quick Details Slide-Over Drawer & Modal States
  const [showPlaybookDrawer, setShowPlaybookDrawer] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [showActionDropdown, setShowActionDropdown] = useState(false);

  // Targets Modal States
  const [showTargetsModal, setShowTargetsModal] = useState(false);
  const [targetModalCategory, setTargetModalCategory] = useState<string>("ALL");
  const [targetModalSearch, setTargetModalSearch] = useState<string>("");
  const [targetModalEnv, setTargetModalEnv] = useState<string>("ALL");
  const [targetModalAccess, setTargetModalAccess] = useState<string>("ALL");
  const [expandedTargetIds, setExpandedTargetIds] = useState<Record<string, boolean>>({});
  const [showRawDetailsIds, setShowRawDetailsIds] = useState<Record<string, boolean>>({});
  const [copiedTargetId, setCopiedTargetId] = useState<string | null>(null);

  // Playbook Selection State
  const [selectedPlaybookItemIds, setSelectedPlaybookItemIds] = useState<string[]>([
    "methodology",
    "sow_lite",
    "readiness",
    "nurture"
  ]);

  // Client Answers Tab UI states
  const [showFieldKeys, setShowFieldKeys] = useState(false);

  const handleOpenLostModal = () => {
    setLostReason("");
    setLostNote("");
    setLostModalOpen(true);
  };

  const handleCloseLostModal = () => {
    setLostModalOpen(false);
    setLostReason("");
    setLostNote("");
  };

  const handleSubmitLost = async () => {
    if (!id) return;
    if (!lostReason) {
      toast.error("Please select a lost reason");
      return;
    }
    if (lostReason === "Other" && !lostNote.trim()) {
      toast.error("A note is required when the reason is 'Other'");
      return;
    }

    try {
      setSubmittingLost(true);
      await markAsLost(id, lostReason, lostNote);
      toast.success("Marked as Lost successfully");
      handleCloseLostModal();
      loadDetails();
    } catch (err: any) {
      console.error("Failed to mark as lost:", err);
      toast.error(err.response?.data?.message || "Failed to mark scoping session as lost");
    } finally {
      setSubmittingLost(false);
    }
  };

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  // Generate dynamic react-pdf Blob url when data is available
  useEffect(() => {
    if (!data) return;
    let active = true;
    let url: string | null = null;

    const buildPdf = async () => {
      try {
        const docNode = (
          <NdaPdfDocument
            clientName={data.nda_signer_name || data.client_name}
            clientAddress={data.nda_signer_address || ""}
            signerName={data.nda_signer_name}
            signerAddress={data.nda_signer_address}
            signatureBase64={data.nda_signature_data}
            signedAt={data.nda_signed_at}
            ssSignerName={data.nda_ss_signer_name}
            ssSignatureBase64={data.nda_ss_signature_data}
            ssSignedAt={data.nda_ss_signed_at}
            signerCompanyName={data.client_name}
          />
        );
        const blob = await pdf(docNode).toBlob();
        if (active) {
          url = URL.createObjectURL(blob);
          setPdfUrl(url);
        }
      } catch (err) {
        console.error("Error building NDA PDF:", err);
      }
    };

    buildPdf();

    return () => {
      active = false;
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [data]);

  // Live Overrides States
  const [region, setRegion] = useState("NZ");
  const [competitiveMode, setCompetitiveMode] = useState(false);
  const [continuousACV, setContinuousACV] = useState(false);
  const [manualCoefficient, setManualCoefficient] = useState(1.0);
  const [manualOverridePrice, setManualOverridePrice] = useState<number | "">("");

  // Tab states: overview | answers | sizing
  const [activeTab, setActiveTab] = useState<"overview" | "answers" | "sizing">("overview");

  // SoW states
  const [sowStatus, setSowStatus] = useState<SowStatus | null>(null);
  const [sowVersion, setSowVersion] = useState<number>(0);
  const [sowBlob, setSowBlob] = useState<SowBlob | null>(null);
  const [sowModalOpen, setSowModalOpen] = useState(false);

  // Access Control States
  const [ctbUsers, setCtbUsers] = useState<any[]>([]);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [isAccessDropdownOpen, setIsAccessDropdownOpen] = useState(false);
  const [isUpdatingAccess, setIsUpdatingAccess] = useState(false);

  // NDA Toggle State
  const [isTogglingNda, setIsTogglingNda] = useState(false);

  const handleDownloadJSON = () => {
    if (!data) return;

    const exportData: Record<string, any> = {
      sessionId: data.id,
      clientName: data.client_name,
      clientEmail: data.client_email,
      submittedAt: data.submitted_at,
      status: data.status,
      answers: {}
    };

    (data.schema_snapshot?.sections || []).forEach((sec: any) => {
      (sec.questions || []).forEach((q: any) => {
        const ansVal = data.answers?.[q.qid];
        if (ansVal === undefined || ansVal === null) return;

        let finalAns = String(ansVal);
        let matchedFlags: string[] = [];

        if (q.type === "single_choice") {
          const opt = (q.options || []).find((o: any) => o.value === ansVal);
          if (opt) {
            finalAns = opt.label;
            matchedFlags = opt.flags || [];
          }
        } else if (q.type === "multi_choice" && Array.isArray(ansVal)) {
          finalAns = ansVal.map((v) => {
            const opt = (q.options || []).find((o: any) => o.value === v);
            return opt ? opt.label : v;
          }).join(", ");
        } else if (q.type === "target_builder" && Array.isArray(ansVal)) {
          finalAns = ansVal.map((asset: any, i: number) => {
            const scopes = (asset.assessment_scopes || []).map((scope: any) => {
              const mLabel = METHODOLOGY_LABELS[scope.assessment_type] || scope.assessment_type || "Unknown";
              const targets = (scope.targets || []).map((t: any) => t.target_value).join(", ");
              return `  ${mLabel}: ${targets}`;
            }).join("\n");
            return `Asset ${i + 1}: ${asset.asset_name || "Unnamed"}\n${scopes}`;
          }).join("\n\n");
        }

        exportData.answers[q.qid] = {
          section: sec.id,
          question: q.label,
          rawValue: ansVal,
          formattedAnswer: finalAns,
          flags: matchedFlags
        };
      });
    });

    const jsonString = `data:text/json;charset=utf-8,${encodeURIComponent(
      JSON.stringify(exportData, null, 2)
    )}`;
    const downloadAnchor = document.createElement("a");
    downloadAnchor.setAttribute("href", jsonString);
    downloadAnchor.setAttribute("download", `${data.client_name?.replace(/\s+/g, "_") || "client"}_scoping_submission.json`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  };

  const loadDetails = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await getResponseById(id);
      setData(res);

      const p = res.enrichment?.pricing;
      if (p) {
        setRegion(p.region || "NZ");
        setCompetitiveMode(p.competitiveMode || false);
        setContinuousACV(p.continuousACV || false);
        setManualCoefficient(p.manualCoefficient ?? 1.0);
        setManualOverridePrice(p.manualOverridePrice ?? "");
      }

      try {
        const sow = await getSow(id);
        setSowStatus(sow.sow_status);
        setSowVersion(sow.sow_version);
        setSowBlob(sow.sow_data);
      } catch (_) {
        // SoW not yet created — silently ignore
      }
    } catch (err) {
      console.error("Failed to load scoping session details:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      if (currentUserRole && [2, 6, 7, 8].includes(currentUserRole)) {
        const res = await axios.get("/v2/companies/my-company/team-members");
        setCtbUsers(res.data?.teamMembers || []);
      } else {
        const res = await axios.get("/v2/user/all?limit=1000&active=true");
        setCtbUsers(res.data?.users || []);
      }
    } catch (err) {
      console.error("Failed to load users:", err);
    }
  };

  useEffect(() => {
    loadDetails();
  }, [id]);

  useEffect(() => {
    if (currentUserRole !== undefined && currentUserRole !== null) {
      loadUsers();
    }
  }, [id, currentUserRole]);

  const handleUpdateAccess = async (updatedIds: number[], actionType: "grant" | "revoke", userName: string) => {
    if (!id) return;
    setIsUpdatingAccess(true);
    const toastId = toast.loading("Updating access control...");
    try {
      await delegateResponse(id, updatedIds);
      toast.success(
        actionType === "grant"
          ? `Read access granted to ${userName}`
          : `Read access revoked for ${userName}`,
        { id: toastId }
      );
      const res = await getResponseById(id);
      setData(res);
    } catch (err: any) {
      console.error("Failed to update delegation access:", err);
      toast.error(err?.response?.data?.message || "Failed to update access control", { id: toastId });
    } finally {
      setIsUpdatingAccess(false);
    }
  };

  const handleToggleNda = async () => {
    if (!id || !data) return;
    setIsTogglingNda(true);
    const targetVal = !data.nda_required;
    const toastId = toast.loading(targetVal ? "Enabling NDA requirement..." : "Disabling NDA requirement...");
    try {
      const res = await toggleNdaRequired(id, targetVal);
      toast.success(res.message, { id: toastId });
      setData((prev: any) => ({
        ...prev,
        nda_required: res.nda_required,
        nda_status: res.nda_status,
        nda_enabled_after_creation: res.nda_enabled_after_creation
      }));
    } catch (err: any) {
      console.error("Failed to toggle NDA:", err);
      toast.error(err?.response?.data?.message || "Failed to update NDA settings", { id: toastId });
    } finally {
      setIsTogglingNda(false);
    }
  };

  const handleApplyPricingOverrides = async () => {
    if (!id) return;
    if (manualOverridePrice !== "" && Number(manualOverridePrice) < 0) {
      toast.error("Manual override price cannot be negative.");
      return;
    }
    try {
      const payload = {
        region,
        competitiveMode,
        continuousACV,
        manualCoefficient,
        manualOverridePrice: manualOverridePrice === "" ? null : Number(manualOverridePrice)
      };

      const res = await updatePricingOverrides(id, payload);
      setData((prev: any) => ({
        ...prev,
        enrichment: res.enrichment
      }));
      toast.success("Pricing overrides applied successfully!");
    } catch (err: any) {
      console.error("Failed to apply pricing overrides:", err);
      toast.error(err?.response?.data?.message || "Failed to apply pricing overrides");
    }
  };

  const handleConvert = async () => {
    if (!id) return;
    setIsConverting(true);
    try {
      await convertToProgram(id);
      toast.success("Scoping questionnaire successfully converted into an Active Security Program!");
      loadDetails();
    } catch (err: any) {
      console.error("Conversion failed:", err);
      toast.error(err?.response?.data?.message || "Conversion failed. Please try again.");
    } finally {
      setIsConverting(false);
    }
  };

  const handleReject = async () => {
    if (!id) return;
    setIsRejecting(true);
    try {
      await rejectResponse(id);
      toast.success("Scoping questionnaire response rejected.");
      loadDetails();
    } catch (err: any) {
      console.error("Rejection failed:", err);
      toast.error(err?.response?.data?.message || "Rejection failed. Please try again.");
    } finally {
      setIsRejecting(false);
    }
  };

  const formatDateToLocalInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const openExtendModal = () => {
    let initialDate = new Date();
    if (data?.expires_at) {
      const currentExpiry = new Date(data.expires_at);
      if (currentExpiry > new Date()) {
        initialDate = currentExpiry;
      } else {
        initialDate.setDate(initialDate.getDate() + 7);
      }
    } else {
      initialDate.setDate(initialDate.getDate() + 7);
    }
    setCustomExpiryDate(formatDateToLocalInput(initialDate));
    setIsExtendModalOpen(true);
  };

  const adjustExpiryDate = (days: number) => {
    let base = new Date();
    if (customExpiryDate) {
      base = new Date(customExpiryDate);
    } else if (data?.expires_at && new Date(data.expires_at) > new Date()) {
      base = new Date(data.expires_at);
    }
    base.setDate(base.getDate() + days);
    setCustomExpiryDate(formatDateToLocalInput(base));
  };

  const getExpiryDiffText = () => {
    if (!customExpiryDate) return null;
    const current = data?.expires_at ? new Date(data.expires_at) : new Date();
    const currentMidnight = new Date(current.getFullYear(), current.getMonth(), current.getDate());

    const selectedDate = new Date(customExpiryDate + "T00:00:00");
    const selectedMidnight = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());

    const diffTime = selectedMidnight.getTime() - currentMidnight.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > 0) {
      return (
        <span className="text-[11px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-lg px-2 py-1">
          Extending by {diffDays} day{diffDays !== 1 ? "s" : ""}
        </span>
      );
    } else if (diffDays < 0) {
      const absDays = Math.abs(diffDays);
      return (
        <span className="text-[11px] font-bold text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1">
          Decreasing by {absDays} day{absDays !== 1 ? "s" : ""}
        </span>
      );
    }
    return (
      <span className="text-[11px] font-bold text-slate-500 bg-slate-50 border border-slate-100 rounded-lg px-2 py-1">
        No change
      </span>
    );
  };

  const handleExtendExpiry = async () => {
    if (!id) return;
    setIsExtending(true);
    try {
      const res = await extendResponseExpiry(id, undefined, customExpiryDate);
      toast.success(res?.message || "Expiry date updated successfully.");
      setIsExtendModalOpen(false);
      loadDetails();
    } catch (err: any) {
      console.error("Extend expiry failed:", err);
      toast.error(err?.response?.data?.message || "Failed to update expiry.");
    } finally {
      setIsExtending(false);
    }
  };

  const handleCopyQuote = () => {
    if (!data) return;
    const finalPriceVal = pricing.manualOverridePrice !== undefined && pricing.manualOverridePrice !== null
      ? pricing.manualOverridePrice
      : pricing.standard?.adjustedPrice || 12000;
    const curr = pricing.standard?.currency || activeCurrency || "NZD";
    const textToCopy = `${curr} $${finalPriceVal.toLocaleString()}`;
    navigator.clipboard.writeText(textToCopy);
    toast.success(`Copied quote to clipboard: ${textToCopy}`);
  };

  const togglePlaybookItem = (itemId: string, locked?: boolean) => {
    if (locked) return;
    setSelectedPlaybookItemIds((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center p-8 text-slate-500">
        <Loader2 className="animate-spin text-slate-700 mb-3" size={32} />
        <p className="text-sm font-semibold text-slate-700">Loading scoping session...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center p-8 space-y-4">
        <p className="text-base text-slate-700 font-bold">Scoping session not found.</p>
        <button
          onClick={() => navigate("/dashboard/sales")}
          className="px-4 py-2 bg-slate-900 text-white text-xs font-semibold rounded-lg hover:bg-slate-800 transition flex items-center gap-2"
        >
          <ArrowLeft size={14} /> Back to leads
        </button>
      </div>
    );
  }

  const en = data.enrichment || {};
  const pricing = en.pricing || {};
  const scorecard = en.scorecard || {};
  const playbook = en.playbook || {};
  const internalSummary = en.internalSummary || {};

  let currentDelegateIds: number[] = [];
  try {
    const parsed = data.delegated_user_ids ? JSON.parse(data.delegated_user_ids) : [];
    currentDelegateIds = Array.isArray(parsed) ? parsed.map((id: any) => Number(id)) : [];
  } catch (e) {
    currentDelegateIds = [];
  }

  const creatorUser = ctbUsers.find(u => u.user_id === data.created_by) || (data.creator ? {
    user_id: data.created_by,
    display_name: data.creator.display_name,
    email: data.creator.email,
    username: data.creator.display_name || "Creator",
    role: 10
  } : null);

  const clientUser = ctbUsers.find(u => {
    if (data.client_email && u.email && u.email.toLowerCase().trim() === data.client_email.toLowerCase().trim()) return true;
    if (data.delegated_to_user_id === u.user_id && [2, 6, 7, 8].includes(u.role)) return true;
    return false;
  }) || (data.client_email ? {
    user_id: data.delegated_to_user_id || -1,
    display_name: data.client_name || "Client",
    email: data.client_email,
    username: data.client_email,
    role: 2
  } : null);

  const delegatedUsers = ctbUsers.filter(u => {
    if (u.user_id === data.created_by) return false;
    if (clientUser && u.user_id === clientUser.user_id) return false;
    return currentDelegateIds.includes(u.user_id);
  });

  const allUsersWithAccess = [
    ...(creatorUser ? [{ ...creatorUser, isCreator: true }] : []),
    ...(clientUser ? [{ ...clientUser, isClient: true }] : []),
    ...delegatedUsers.map(u => ({ ...u, isDelegate: true }))
  ];

  const remainingUsers = ctbUsers.filter(u => {
    if (u.role === 10) return false;
    if (currentDelegateIds.includes(u.user_id)) return false;
    if (data.created_by === u.user_id) return false;
    if (data.delegated_to_user_id === u.user_id && [2, 6, 7, 8].includes(u.role)) return false;
    if (data.client_email && u.email && u.email.toLowerCase().trim() === data.client_email.toLowerCase().trim()) return false;
    return true;
  });

  const filteredRemainingUsers = remainingUsers.filter(u => {
    const query = userSearchQuery.toLowerCase();
    return (
      (u.username || "").toLowerCase().includes(query) ||
      (u.email || "").toLowerCase().includes(query) ||
      (u.display_name || "").toLowerCase().includes(query)
    );
  });

  const roleLabel = (roleNum: number) => {
    switch (roleNum) {
      case 1: return "Researcher";
      case 2: return "Business Owner";
      case 3: return "Admin";
      case 4: return "QA";
      case 5: return "Admin Manager";
      case 6: return "Security Engineer";
      case 7: return "Program Manager";
      case 8: return "Org Admin";
      case 9: return "Sub Admin";
      case 10: return "Solutions Specialist";
      default: return "User";
    }
  };

  const currentPriceObj = pricing.standard || {};
  const finalPrice = pricing.manualOverridePrice !== undefined && pricing.manualOverridePrice !== null
    ? pricing.manualOverridePrice
    : pricing.standard?.adjustedPrice || 12000;
  const floorPrice = currentPriceObj.guardrails?.floor || 10800;
  const ceilingPrice = currentPriceObj.guardrails?.ceiling || 13200;
  const activeCurrency = currentPriceObj.currency || REGION_DAILY_RATES[region]?.currency || "NZD";

  const priceLow = pricing.priceLow || floorPrice || 10800;
  const priceHigh = pricing.priceHigh || ceilingPrice || 13200;

  // Extract real dynamic answers from response data
  const answersObj = data?.answers || {};
  const rawBusinessAssets: any[] = Array.isArray(answersObj.business_assets)
    ? answersObj.business_assets
    : Array.isArray(answersObj.business_assets?.rawValue)
    ? answersObj.business_assets.rawValue
    : Array.isArray(answersObj.target_builder)
    ? answersObj.target_builder
    : Array.isArray(answersObj.target_builder?.rawValue)
    ? answersObj.target_builder.rawValue
    : [];

  let totalTargetCount = 0;
  const targetTypeCounts: Record<string, { count: number; label: string; icon: string }> = {};

  if (rawBusinessAssets.length > 0) {
    rawBusinessAssets.forEach((asset: any) => {
      const scopes = Array.isArray(asset.assessment_scopes) && asset.assessment_scopes.length > 0
        ? asset.assessment_scopes
        : [{ assessment_type: asset.assessment_type || asset.asset_type || "WEB_APP", targets: asset.targets || [] }];

      scopes.forEach((sc: any) => {
        const rawType = String(sc.assessment_type || asset.assessment_type || "WEB_APP").toUpperCase();
        let label = METHODOLOGY_LABELS[rawType] || sc.assessment_type || asset.assessment_type || rawType.replace(/_/g, " ");
        let icon = "🌐";
        if (rawType.includes("API")) icon = ">_";
        else if (rawType.includes("MOBILE")) icon = "📱";
        else if (rawType.includes("NET")) icon = "🏢";
        else if (rawType.includes("CLOUD")) icon = "☁️";
        else if (rawType.includes("CODE")) icon = "📂";

        const tCount = Array.isArray(sc.targets) && sc.targets.length > 0 ? sc.targets.length : 1;
        totalTargetCount += tCount;

        if (targetTypeCounts[rawType]) {
          targetTypeCounts[rawType].count += tCount;
        } else {
          targetTypeCounts[rawType] = { count: tCount, label, icon };
        }
      });
    });
  }

  if (Object.keys(targetTypeCounts).length === 0) {
    targetTypeCounts.WEB_APP = { count: 1, label: "Web application pentest", icon: "🌐" };
    totalTargetCount = 1;
  }

  const attachedCount = selectedPlaybookItemIds.length;

  // Helper to ensure values rendered in React DOM are clean strings/primitives
  const formatReactChild = (val: any, fallback: string = ""): string => {
    if (val === undefined || val === null) return fallback;
    if (typeof val === "string" || typeof val === "number" || typeof val === "boolean") {
      const s = String(val).trim();
      return s ? s : fallback;
    }
    if (typeof val === "object") {
      if (val.formattedAnswer !== undefined && val.formattedAnswer !== null) {
        return formatReactChild(val.formattedAnswer, fallback);
      }
      if (val.rawValue !== undefined && val.rawValue !== null) {
        return formatReactChild(val.rawValue, fallback);
      }
      if (Array.isArray(val)) {
        const items = val.map((item) => formatReactChild(item)).filter(Boolean);
        return items.length > 0 ? items.join(", ") : fallback;
      }
      if (val.name || val.role) {
        if (val.name && val.role) return `${val.name} (${val.role})`;
        if (val.name) return String(val.name);
        if (val.role) return String(val.role);
      }
      if (val.label) return String(val.label);
      if (val.title) return String(val.title);
      if (val.value) return String(val.value);

      const nonNullEntries = Object.entries(val).filter(([_, v]) => v !== null && v !== undefined && v !== "");
      if (nonNullEntries.length === 0) {
        return fallback;
      }
      try {
        return JSON.stringify(val);
      } catch (_) {
        return fallback;
      }
    }
    return String(val);
  };

  const formatSizeAndBand = (sc: any, target: any, asset: any): string => {
    const details = sc?.details || {};
    const classification = details.__classification;

    if (classification && Array.isArray(classification.sizeReasons) && classification.sizeReasons.length > 0 && classification.sizeReasons[0]) {
      return String(classification.sizeReasons[0]);
    }

    if (details.pages_count_band) {
      const v = String(details.pages_count_band);
      if (v === "1_10") return "1–10 pages";
      if (v === "11_30") return "11–30 pages";
      if (v === "31_75") return "31–75 pages";
      if (v === "75_plus") return "75+ pages";
      return `${v.replace(/_/g, " ")} pages`;
    }
    if (details.code_size_band) {
      const v = String(details.code_size_band);
      if (v === "lte_50k") return "≤50K LOC";
      if (v === "50k_200k") return "50K–200K LOC";
      if (v === "200k_plus") return "200K+ LOC";
      return `${v.replace(/_/g, " ")} LOC`;
    }
    if (details.endpoints_count_band) {
      const v = String(details.endpoints_count_band);
      if (v === "1_20") return "1–20 endpoints";
      if (v === "21_100") return "21–100 endpoints";
      if (v === "101_300") return "101–300 endpoints";
      if (v === "300_plus") return "300+ endpoints";
      return `${v.replace(/_/g, " ")} endpoints`;
    }
    if (details.live_hosts_count_band) {
      const v = String(details.live_hosts_count_band);
      if (v === "1_5") return "1–5 live hosts";
      if (v === "6_25") return "6–25 live hosts";
      if (v === "26_100") return "26–100 live hosts";
      if (v === "100_plus") return "100+ live hosts";
      return `${v.replace(/_/g, " ")} live hosts`;
    }
    if (details.hosts_count_band) {
      const v = String(details.hosts_count_band);
      if (v === "1_50") return "1–50 internal hosts";
      if (v === "51_250") return "51–250 internal hosts";
      if (v === "251_1000") return "251–1000 internal hosts";
      if (v === "1000_plus") return "1000+ internal hosts";
      return `${v.replace(/_/g, " ")} internal hosts`;
    }
    if (details.accounts_count_band) {
      const v = String(details.accounts_count_band);
      if (v === "1") return "1 account";
      if (v === "2_5") return "2–5 accounts";
      if (v === "6_20") return "6–20 accounts";
      if (v === "20_plus") return "20+ accounts";
      return `${v.replace(/_/g, " ")} accounts`;
    }

    if (classification?.label) return classification.label;
    if (classification?.size) return classification.size;

    const explicitSize = (typeof target === "object" && (target.scope_size || target.size)) || sc?.scope_size || asset?.scope_size;
    if (explicitSize) return formatReactChild(explicitSize);

    return "Standard";
  };

  const formatAccessStatus = (sc: any, target: any, asset: any): "Yes" | "No" | "N/A" => {
    const details = sc?.details || {};
    const loginReq = details.login_required ?? (typeof target === "object" ? target.requires_auth : undefined) ?? sc?.requires_auth ?? asset?.requires_auth;
    const hasAccessLevels = Array.isArray(target?.access_levels) && target.access_levels.length > 0;
    const approach = (typeof target === "object" && target.testing_approach) || sc?.testing_approach || asset?.testing_approach;
    const hasCreds = (typeof target === "object" && target.has_credentials) || sc?.has_credentials || asset?.has_credentials;

    if (
      loginReq === true ||
      loginReq === "yes" ||
      loginReq === "Yes" ||
      hasAccessLevels ||
      approach === "GREY_BOX" ||
      approach === "WHITE_BOX" ||
      hasCreds === true
    ) {
      return "Yes";
    }

    if (
      loginReq === false ||
      loginReq === "no" ||
      loginReq === "No" ||
      (approach === "BLACK_BOX" && !hasAccessLevels)
    ) {
      return "No";
    }

    const rawAcc = typeof target === "object" ? (target.access_status || target.access || sc?.access || asset?.access) : undefined;
    if (rawAcc && typeof rawAcc === "string" && rawAcc.trim() !== "") {
      const l = rawAcc.toLowerCase();
      if (l.includes("yes") || l.includes("req") || l.includes("auth") || l.includes("pend") || l.includes("grey") || l.includes("white")) {
        return "Yes";
      }
      if (l.includes("no") || l.includes("none") || l.includes("black")) {
        return "No";
      }
    }

    return "N/A";
  };

  const formatEnvironment = (rawEnv: any, rawType: string): string => {
    if (!rawEnv) {
      if (rawType.includes("CODE")) return "Source Repo";
      return "Production";
    }
    const s = String(rawEnv).trim().toUpperCase();
    if (s.includes("PROD") && !s.includes("PRE")) return "Production";
    if (s.includes("STAG")) return "Staging";
    if (s.includes("PRE_PROD") || s.includes("PREPROD") || s.includes("PRE-PROD")) return "Pre-prod";
    if (s.includes("DEV")) return "Development";
    if (s.includes("UAT")) return "UAT";
    if (s.includes("TEST")) return "Test";
    if (s.includes("QA")) return "QA";
    return s.charAt(0) + s.slice(1).toLowerCase();
  };

  const formatOwner = (rawOwner: any, fallback: string): string => {
    if (!rawOwner) return fallback;
    if (typeof rawOwner === "string") {
      const trimmed = rawOwner.trim();
      if (!trimmed || trimmed === "{}" || trimmed === '{"name":null,"role":null}') return fallback;
      return trimmed;
    }
    if (typeof rawOwner === "object") {
      const name = rawOwner.name ? String(rawOwner.name).trim() : "";
      const role = rawOwner.role ? String(rawOwner.role).trim() : "";
      const email = rawOwner.email ? String(rawOwner.email).trim() : "";
      if (name && role) return `${name} (${role})`;
      if (name) return name;
      if (email) return email;
      if (role) return role;
      return fallback;
    }
    return fallback;
  };

  // Dynamic values extracted from actual response answers
  const rawClientName = answersObj.client_name || data.client_name || answersObj.company_name || answersObj.company;
  const clientName = formatReactChild(rawClientName, "Client");

  const rawSubmitterName = answersObj.client_contact || answersObj.contact_name || answersObj.name || data.nda_signer_name || (typeof answersObj.role === "object" ? answersObj.role?.name : undefined);
  const submitterName = formatReactChild(rawSubmitterName, clientName);

  const rawClientEmail = answersObj.client_email || data.client_email || answersObj.contact_email || answersObj.email;
  const clientEmail = formatReactChild(rawClientEmail, "N/A");

  const rawClientRole = answersObj.client_role || answersObj.contact_role || answersObj.contact_title || (typeof answersObj.role === "object" ? answersObj.role?.role : answersObj.role);
  const clientRole = formatReactChild(rawClientRole, "CISO");

  const urgencyWindow = formatReactChild(answersObj.urgency_window || answersObj.timeframe || answersObj.urgency, "Within 2 weeks");
  const headcountBand = formatReactChild(answersObj.headcount_band || answersObj.company_size || answersObj.headcount, "1–10");
  const engagementPref = formatReactChild(answersObj.engagement_preference || answersObj.engagement_model || answersObj.testing_type, "One-time pentest — single window");

  // Dynamic parsing of ALL targets from real response data
  const parsedTargets: Array<{
    id: string;
    name: string;
    address: string;
    type: string;
    typeLabel: string;
    icon: string;
    environment: string;
    accessStatus: "Yes" | "No" | "N/A";
    size: string;
    owner: string;
    targetId?: string;
    targetKind?: string;
    exposure?: string | null;
    testingApproach?: string;
    accessLevels?: string[];
    details?: any;
    scopeId?: string;
    assetName?: string;
    assetCategory?: string;
    criticality?: string | null;
  }> = (() => {
    const targetsList: Array<any> = [];

    const rawAssets: any[] = rawBusinessAssets;

    if (rawAssets.length > 0) {
      rawAssets.forEach((asset: any, aIdx: number) => {
        const assetName = asset.asset_name || asset.name || `Target ${aIdx + 1}`;
        const scopes = Array.isArray(asset.assessment_scopes) && asset.assessment_scopes.length > 0
          ? asset.assessment_scopes
          : [{ assessment_type: asset.assessment_type || asset.asset_type || "WEB_APP", targets: asset.targets || [] }];

        scopes.forEach((sc: any, sIdx: number) => {
          const rawType = String(sc.assessment_type || asset.assessment_type || asset.asset_type || "WEB_APP").toUpperCase();
          const typeLabel = METHODOLOGY_LABELS[rawType] || sc.assessment_type || asset.assessment_type || rawType.replace(/_/g, " ");
          let icon = "🌐";
          if (rawType.includes("API")) icon = ">_";
          else if (rawType.includes("MOBILE")) icon = "📱";
          else if (rawType.includes("NET")) icon = "🏢";
          else if (rawType.includes("CLOUD")) icon = "☁️";
          else if (rawType.includes("CODE")) icon = "📂";

          const rawTargets = Array.isArray(sc.targets) && sc.targets.length > 0
            ? sc.targets
            : (Array.isArray(asset.targets) && asset.targets.length > 0 ? asset.targets : [{ target_value: asset.target_url || asset.url || assetName }]);

          rawTargets.forEach((t: any, tIdx: number) => {
            const rawVal = typeof t === "string" ? t : (t.target_value || t.url || t.name || t.address || assetName);
            const tVal = formatReactChild(rawVal, formatReactChild(assetName, "app.client.com"));
            const rawName = typeof t === "object" && t.name ? t.name : (assetName !== tVal ? assetName : (typeof tVal === "string" ? tVal.replace(/^https?:\/\//, "").split("/")[0] : assetName));
            const tName = formatReactChild(rawName, "Target");

            const rawEnv = (typeof t === "object" && (t.environment_stage || t.environment)) || sc.environment_stage || sc.environment || asset.environment_stage || asset.environment;
            const env = formatEnvironment(rawEnv, rawType);
            const acc = formatAccessStatus(sc, t, asset);
            const sz = formatSizeAndBand(sc, t, asset);
            const own = formatOwner((typeof t === "object" && t.owner) || asset.owner, submitterName || clientName || "Client");

            targetsList.push({
              id: `${aIdx}-${sIdx}-${tIdx}`,
              name: tName,
              address: tVal,
              type: rawType,
              typeLabel: formatReactChild(typeLabel, "Target"),
              icon,
              environment: env,
              accessStatus: acc,
              size: sz,
              owner: own,
              targetId: typeof t === "object" ? t.target_id : undefined,
              targetKind: typeof t === "object" ? t.target_kind : undefined,
              exposure: typeof t === "object" ? t.exposure : undefined,
              testingApproach: typeof t === "object" ? t.testing_approach : sc.testing_approach,
              accessLevels: typeof t === "object" && Array.isArray(t.access_levels) ? t.access_levels : [],
              details: sc.details || asset.details || {},
              scopeId: sc.scope_id,
              assetName: assetName,
              assetCategory: asset.asset_category,
              criticality: asset.criticality
            });
          });
        });
      });
    }

    if (targetsList.length === 0) {
      targetsList.push({
        id: "default-1",
        name: formatReactChild(clientName, "Primary Web Application"),
        address: formatReactChild(answersObj.target_url || answersObj.url, "app.client.com"),
        type: "WEB_APP",
        typeLabel: "Web application",
        icon: "🌐",
        environment: "Production",
        accessStatus: "N/A",
        size: "Standard",
        owner: formatReactChild(submitterName || clientName, "Client"),
        details: {}
      });
    }

    return targetsList;
  })();

  // Dynamic SOW modules derived from parsed targets/assets
  const sowModules = (() => {
    const mods: any[] = [];
    rawBusinessAssets.forEach((asset: any) => {
      const assetName = asset.asset_name || asset.name || "Asset";
      const scopes = Array.isArray(asset.assessment_scopes) && asset.assessment_scopes.length > 0
        ? asset.assessment_scopes
        : [{ assessment_type: asset.assessment_type || asset.asset_type || "WEB_APP", targets: asset.targets || [] }];

      scopes.forEach((sc: any) => {
        const rawType = String(sc.assessment_type || asset.assessment_type || "WEB_APP").toUpperCase();
        let normalizedType = "WEB_APP";
        if (rawType.includes("WEB")) normalizedType = "WEB_APP";
        else if (rawType.includes("API")) normalizedType = "API";
        else if (rawType.includes("MOBILE")) normalizedType = "MOBILE_APP";
        else if (rawType.includes("EXTERNAL")) normalizedType = "EXTERNAL_NETWORK";
        else if (rawType.includes("INTERNAL")) normalizedType = "INTERNAL_NETWORK";
        else if (rawType.includes("CLOUD_PENTEST") || rawType.includes("INFRASTRUCTURE")) normalizedType = "CLOUD_PENTEST";
        else if (rawType.includes("CLOUD")) normalizedType = "CLOUD_CONFIG";
        else if (rawType.includes("AI") || rawType.includes("LLM")) normalizedType = "AI_LLM";
        else if (rawType.includes("CODE")) normalizedType = "CODE_REVIEW";

        const targets = Array.isArray(sc.targets) && sc.targets.length > 0
          ? sc.targets.map((t: any) => typeof t === "string" ? t : (t.target_value || t.url || assetName))
          : [assetName];

        const rawEnv = sc.environment_stage || sc.environment || asset.environment_stage || asset.environment || (sc.targets?.[0]?.environment_stage);
        const env = formatEnvironment(rawEnv, rawType);
        const isGray = !!(
          sc.access_type === "graybox" ||
          sc.testing_approach === "GREY_BOX" ||
          sc.testing_approach === "WHITE_BOX" ||
          sc.details?.login_required === true ||
          (Array.isArray(sc.targets?.[0]?.access_levels) && sc.targets[0].access_levels.length > 0)
        );

        mods.push({
          assetName,
          assessmentType: normalizedType,
          isGraybox: isGray,
          targets,
          environment: env,
          status: "Confirmed",
          description: `Assessment of ${assetName} across identified endpoints and business workflows.`
        });
      });
    });
    return mods;
  })();

  const targetCategories: Record<string, number> = (() => {
    const cats: Record<string, number> = {};
    parsedTargets.forEach((t) => {
      cats[t.typeLabel] = (cats[t.typeLabel] || 0) + 1;
    });
    return cats;
  })();

  const filteredModalTargets = parsedTargets.filter((t) => {
    if (targetModalCategory !== "ALL" && t.typeLabel !== targetModalCategory) return false;
    if (targetModalEnv !== "ALL" && t.environment !== targetModalEnv) return false;
    if (targetModalAccess !== "ALL" && t.accessStatus !== targetModalAccess) return false;
    if (targetModalSearch.trim()) {
      const q = targetModalSearch.toLowerCase();
      const matchName = t.name.toLowerCase().includes(q);
      const matchAddr = t.address.toLowerCase().includes(q);
      const matchOwner = t.owner.toLowerCase().includes(q);
      const matchType = t.typeLabel.toLowerCase().includes(q);
      if (!matchName && !matchAddr && !matchOwner && !matchType) return false;
    }
    return true;
  });

  const allAreExpanded = filteredModalTargets.length > 0 && filteredModalTargets.every((t) => !!expandedTargetIds[t.id]);

  const toggleTargetExpand = (targetId: string) => {
    setExpandedTargetIds((prev) => ({
      ...prev,
      [targetId]: !prev[targetId]
    }));
  };

  const handleExpandAll = () => {
    const allExpanded: Record<string, boolean> = {};
    filteredModalTargets.forEach((t) => {
      allExpanded[t.id] = true;
    });
    setExpandedTargetIds(allExpanded);
  };

  const handleCollapseAll = () => {
    setExpandedTargetIds({});
  };

  const toggleRawDetails = (targetId: string) => {
    setShowRawDetailsIds((prev) => ({
      ...prev,
      [targetId]: !prev[targetId]
    }));
  };

  const handleCopyRawDetails = (targetId: string, obj: any) => {
    navigator.clipboard.writeText(JSON.stringify(obj, null, 2));
    setCopiedTargetId(targetId);
    toast.success("Scope details copied to clipboard");
    setTimeout(() => {
      setCopiedTargetId(null);
    }, 2000);
  };

  const yesAccessCount = parsedTargets.filter(t => t.accessStatus === "Yes").length;
  const noAccessCount = parsedTargets.filter(t => t.accessStatus === "No").length;
  const naAccessCount = parsedTargets.filter(t => t.accessStatus === "N/A").length;

  // Dynamic Questions from schema_snapshot
  const schemaSections: any[] = (data.schema_snapshot?.sections && data.schema_snapshot.sections.length > 0)
    ? data.schema_snapshot.sections
    : [
        { id: "who_submitted", title: "Who submitted this", description: "Contact and company identity from the questionnaire." },
        { id: "why_now", title: "Why now", description: "Timing pressure driving the request." },
        { id: "company_profile", title: "Company profile", description: "Signals used to place this lead in a tier." },
        { id: "engagement_preference", title: "Engagement preference", description: "How the client wants to buy." },
        { id: "targets_in_scope", title: "Targets in scope", description: `${totalTargetCount} asset${totalTargetCount !== 1 ? 's' : ''} nominated for testing.` },
        { id: "authorisation", title: "Authorisation", description: "Signed off by the submitting contact." }
      ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-800 font-sans pb-16 antialiased selection:bg-slate-900 selection:text-white">
      {/* ─── Top Main Navigation Bar ─── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-4">
        
        {/* Header Breadcrumb & Lead Details */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1">
            <button
              onClick={() => navigate("/dashboard/sales")}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900 transition-colors mb-1"
            >
              <ArrowLeft size={13} />
              <span>All leads</span>
            </button>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-xl font-bold text-slate-900 tracking-tight">
                {clientName}
              </h1>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold tracking-wide ${
                data.status === "CONVERTED" || data.status === "Converted"
                  ? "bg-emerald-100 text-emerald-800"
                  : data.status === "REJECTED" || data.status === "LOST"
                  ? "bg-rose-100 text-rose-800"
                  : "bg-emerald-100 text-emerald-800"
              }`}>
                {data.status === "CONVERTED" ? "Converted" : data.status || "Converted"}
              </span>
            </div>
            <p className="text-xs text-slate-500 font-normal">
              {submitterName} · {clientRole} · ✉ {clientEmail} · Submitted {data.submitted_at ? new Date(data.submitted_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : "17 Jun 2026"}
            </p>
          </div>

          {/* Top Right Actions */}
          <div className="flex items-center gap-2 relative shrink-0">
            {/* Slide-over Drawer Trigger Button (Square Icon as seen in Screenshot 3 top right) */}
            <button
              type="button"
              onClick={() => setShowPlaybookDrawer(true)}
              className="p-2.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-semibold shadow-sm transition-all"
              title="Open Proposal Playbook"
            >
              <Edit3 size={15} />
            </button>

            {/* Dropdown Menu Trigger */}
            <button
              type="button"
              onClick={() => setShowActionDropdown(!showActionDropdown)}
              className="p-2.5 border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-semibold shadow-sm transition-all"
              title="More Actions"
            >
              •••
            </button>

            {/* Dropdown Menu for actions */}
            {showActionDropdown && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowActionDropdown(false)} />
                <div className="absolute right-0 top-full mt-1.5 w-56 bg-white border border-slate-200 rounded-xl shadow-xl py-1.5 z-40 text-xs font-medium space-y-0.5">
                  {data.status === "SUBMITTED" && (
                    <button
                      onClick={() => { setShowActionDropdown(false); handleConvert(); }}
                      disabled={isConverting}
                      className="w-full text-left px-3.5 py-2 text-emerald-700 hover:bg-emerald-50 font-semibold flex items-center gap-2"
                    >
                      <CheckCircle2 size={13} /> Convert to program
                    </button>
                  )}
                  <button
                    onClick={() => { setShowActionDropdown(false); openExtendModal(); }}
                    className="w-full text-left px-3.5 py-2 text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                  >
                    <RefreshCw size={13} /> {data?.isExpired ? "Reactivate scope link" : "Extend expiry link"}
                  </button>
                  <button
                    onClick={() => { setShowActionDropdown(false); handleToggleNda(); }}
                    disabled={isTogglingNda}
                    className="w-full text-left px-3.5 py-2 text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                  >
                    <Shield size={13} /> {data.nda_required ? "Disable NDA requirement" : "Require NDA signature"}
                  </button>
                  <button
                    onClick={() => { setShowActionDropdown(false); handleOpenLostModal(); }}
                    className="w-full text-left px-3.5 py-2 text-rose-600 hover:bg-rose-50 font-semibold flex items-center gap-2"
                  >
                    <XCircle size={13} /> Mark Prospect as Lost
                  </button>
                  <button
                    onClick={() => { setShowActionDropdown(false); setIsConfirmRejectOpen(true); }}
                    className="w-full text-left px-3.5 py-2 text-rose-700 hover:bg-rose-50 flex items-center gap-2"
                  >
                    <XCircle size={13} /> Reject scope / Discard draft
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ─── Top 3 Header Summary Metric Cards ─── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
          
          {/* Card 1: Recommended Quote (Dark Box) */}
          <div className="bg-[#0B132B] text-white p-5 rounded-xl shadow-sm flex flex-col justify-between min-h-[140px] relative overflow-hidden">
            <div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400 font-medium">Recommended quote</span>
                <button
                  onClick={handleCopyQuote}
                  className="inline-flex items-center gap-1 bg-white/10 hover:bg-white/20 text-white rounded-md px-2.5 py-1 text-xs font-semibold transition"
                >
                  <Copy size={11} />
                  <span>Copy</span>
                </button>
              </div>
              <div className="mt-2 text-3xl font-bold text-white tracking-tight">
                {activeCurrency} ${finalPrice.toLocaleString()}
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-800/80 flex items-center justify-between text-xs">
              <span className="text-slate-400 font-normal">
                Engine range <strong className="text-slate-200 font-semibold">{activeCurrency} ${priceLow.toLocaleString()} – ${priceHigh.toLocaleString()}</strong>
              </span>
              <span className="text-emerald-400 font-medium inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                Within optimal guardrails
              </span>
            </div>
          </div>

          {/* Card 2: Tier (White Card) */}
          <div className="bg-white border border-slate-200 p-5 rounded-xl shadow-sm flex flex-col justify-between min-h-[140px]">
            <div>
              <span className="text-xs text-slate-400 font-medium block">Tier</span>
              <h2 className="text-2xl font-bold text-slate-900 mt-1">
                {internalSummary.tier || "Startup"}
              </h2>
              <div className="mt-2">
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                  📈 High confidence
                </span>
              </div>
            </div>
            <p className="text-xs text-slate-400 font-normal mt-4">
              Profiled from scoping questionnaire
            </p>
          </div>

          {/* Card 3: Recommended Route (White Card) */}
          <div className="bg-white border border-slate-200 p-5 rounded-xl shadow-sm flex flex-col justify-between min-h-[140px] relative">
            <div>
              <span className="text-xs text-slate-400 font-medium block">Recommended route</span>
              <h2 className="text-base font-bold text-slate-900 mt-1">
                {playbook.route || "Nurture / automated follow-up"}
              </h2>
              <p className="text-xs text-slate-500 font-normal mt-1.5">
                <strong>Next:</strong> {playbook.nextStep || "Send automated security best-practice sequence"}
              </p>
            </div>
            <span className="absolute bottom-3 right-4 text-slate-200 text-xl font-serif">“</span>
          </div>

        </div>

        {/* ─── Navigation Tabs Row (Overview | Client answers | Sizing breakdown) ─── */}
        <div className="mt-6 border-b border-slate-200 flex items-center gap-8">
          <button
            onClick={() => setActiveTab("overview")}
            className={`pb-3 text-xs font-semibold transition-all relative ${
              activeTab === "overview"
                ? "text-slate-900 border-b-2 border-slate-900"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            Overview
          </button>

          <button
            onClick={() => setActiveTab("answers")}
            className={`pb-3 text-xs font-semibold transition-all relative ${
              activeTab === "answers"
                ? "text-slate-900 border-b-2 border-slate-900"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            Client answers
          </button>

          <button
            onClick={() => setActiveTab("sizing")}
            className={`pb-3 text-xs font-semibold transition-all relative ${
              activeTab === "sizing"
                ? "text-slate-900 border-b-2 border-slate-900"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            Sizing breakdown
          </button>
        </div>

        {/* ─── Main Content Grid (Left Column 2/3, Right Column 1/3) ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          
          {/* ── LEFT COLUMN (Main Tab Content) ── */}
          <div className="lg:col-span-2 space-y-6">

            {/* TAB 1: Overview */}
            {activeTab === "overview" && (
              <div className="space-y-6">
                
                {/* Buyer Profile & Score Breakdown Card */}
                <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-6">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide">Intake intelligence</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs">
                    {/* Buyer Profile */}
                    <div className="space-y-3">
                      <h4 className="font-bold text-slate-400 uppercase text-[10px]">Buyer profile</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between border-b border-slate-100 pb-1.5">
                          <span className="text-slate-400">Buyer Type:</span>
                          <span className="font-bold text-slate-800">{internalSummary.buyerType || "Qualified Lead"}</span>
                        </div>
                        <div className="flex justify-between border-b border-slate-100 pb-1.5">
                          <span className="text-slate-400">Willingness Bracket:</span>
                          <span className="font-bold text-indigo-600">{internalSummary.likelyBudgetBracket || "Medium"}</span>
                        </div>
                        <div className="flex justify-between border-b border-slate-100 pb-1.5">
                          <span className="text-slate-400">Client Role:</span>
                          <span className="font-bold text-slate-800">{clientRole}</span>
                        </div>
                        <div className="flex justify-between border-b border-slate-100 pb-1.5">
                          <span className="text-slate-400">Urgency window:</span>
                          <span className="font-bold text-amber-600">{urgencyWindow}</span>
                        </div>
                        <div className="flex justify-between border-b border-slate-100 pb-1.5">
                          <span className="text-slate-400">Headcount:</span>
                          <span className="font-bold text-slate-800">{headcountBand}</span>
                        </div>
                      </div>
                    </div>

                    {/* Score Breakdown */}
                    <div className="space-y-3">
                      <h4 className="font-bold text-slate-400 uppercase text-[10px]">Score breakdown</h4>
                      <div className="space-y-2.5">
                        {[
                          { label: "1. Urgency", score: scorecard.urgency || 85, color: "bg-amber-500" },
                          { label: "2. Capacity", score: scorecard.capacity || 70, color: "bg-emerald-500" },
                          { label: "3. Authority", score: scorecard.authority || 95, color: "bg-blue-500" },
                          { label: "4. Desire", score: scorecard.desire || 80, color: "bg-rose-500" },
                          { label: "5. Complexity", score: scorecard.complexity || 40, color: "bg-purple-500" }
                        ].map((dim, idx) => (
                          <div key={idx} className="space-y-1">
                            <div className="flex justify-between text-[11px] font-semibold text-slate-500">
                              <span>{dim.label}</span>
                              <span className="text-slate-900 font-bold">{Math.round(dim.score)}%</span>
                            </div>
                            <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className={`h-full ${dim.color}`} style={{ width: `${dim.score}%` }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pitch Angle Banner */}
                {internalSummary.salesAngle && (
                  <div className="bg-indigo-50/70 border border-indigo-100 rounded-xl p-4 text-xs text-indigo-900 flex items-start gap-3">
                    <span className="text-indigo-600 font-serif text-2xl leading-none">“</span>
                    <div>
                      <span className="font-bold block text-indigo-950 mb-0.5">Recommended Pitch Angle</span>
                      <p className="italic">{internalSummary.salesAngle}</p>
                    </div>
                  </div>
                )}

                {/* Statement of Work Status Card */}
                <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-4">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">Statement of Work</h3>
                      <p className="text-xs text-slate-400">Formal deliverable proposal document</p>
                    </div>
                    {sowStatus && (
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                        {sowStatus.replace("_", " ")} v{sowVersion}
                      </span>
                    )}
                  </div>

                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="space-y-1 text-xs">
                      <h4 className="font-bold text-slate-900 text-sm">
                        {sowStatus === "FINAL" ? "Statement of Work Finalised" : sowStatus ? "Draft SoW Available" : "No SoW Generated"}
                      </h4>
                      <p className="text-slate-500">
                        {sowStatus
                          ? "Review, edit, or download the formal Statement of Work document."
                          : "Generate a complete Statement of Work pre-populated from this scoping questionnaire response."}
                      </p>
                    </div>
                    <button
                      onClick={() => navigate(`/dashboard/sales/scoping/${id}/sow`)}
                      className="px-4 py-2 bg-[#0B132B] hover:bg-slate-800 text-white rounded-lg text-xs font-bold shrink-0 transition flex items-center gap-2 shadow-sm"
                    >
                      <PenLine size={13} />
                      <span>{sowStatus ? "Open SoW Editor" : "Generate SoW"}</span>
                    </button>
                  </div>
                </div>

                {/* Similar Sessions Table */}
                <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-4">
                  <h3 className="text-sm font-bold text-slate-900">Similar scoping sessions</h3>
                  <div className="overflow-x-auto text-xs">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b border-slate-200 text-slate-400 font-medium text-[11px]">
                          <th className="py-2 px-3">Session</th>
                          <th className="py-2 px-3">Tier</th>
                          <th className="py-2 px-3">Targets</th>
                          <th className="py-2 px-3">Price</th>
                          <th className="py-2 px-3">Status</th>
                          <th className="py-2 px-3">Match</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {(data.historicalDeals || []).length > 0 ? (
                          data.historicalDeals.slice(0, 5).map((deal: any, index: number) => (
                            <tr key={index} className="hover:bg-slate-50">
                              <td className="py-2.5 px-3">
                                <Link to={`/dashboard/sales/scoping/${deal.documentId}`} className="text-indigo-600 font-bold hover:underline">
                                  {deal.identifier}
                                </Link>
                              </td>
                              <td className="py-2.5 px-3">{deal.tier}</td>
                              <td className="py-2.5 px-3">{deal.targetCount || 1} targets</td>
                              <td className="py-2.5 px-3 font-semibold">{deal.price ? `$${deal.price.toLocaleString()}` : "—"}</td>
                              <td className="py-2.5 px-3">
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-700">
                                  {deal.status}
                                </span>
                              </td>
                              <td className="py-2.5 px-3 font-bold text-slate-700">
                                {Math.round((deal.similarityScore || 0.72) * 100)}%
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={6} className="py-6 text-center text-slate-400 italic">
                              No similar sessions found for comparison.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

              </div>
            )}

            {/* TAB 2: Client answers (Strict Sequential Section Loop matching Questionnaire Schema) */}
            {activeTab === "answers" && (
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-6">
                
                {/* Header of Client answers */}
                <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                  <div>
                    <h2 className="text-base font-bold text-slate-900">Client answers</h2>
                    <p className="text-xs text-slate-400 font-normal">Exactly as submitted — nothing here has been edited.</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer select-none text-xs text-slate-500 font-medium">
                      <input
                        type="checkbox"
                        checked={showFieldKeys}
                        onChange={(e) => setShowFieldKeys(e.target.checked)}
                        className="rounded border-slate-300 text-slate-900 focus:ring-slate-900 w-3.5 h-3.5"
                      />
                      <span>Field keys</span>
                    </label>
                    <button
                      onClick={handleDownloadJSON}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-semibold transition shadow-sm"
                    >
                      <Download size={13} />
                      <span>Export JSON</span>
                    </button>
                  </div>
                </div>

                {/* Iterate sequentially over sections */}
                {schemaSections.map((sec: any, secIdx: number) => {
                  const secIdLower = String(sec.id || "").toLowerCase();
                  const secTitleLower = String(sec.title || "").toLowerCase();

                  const isContactSection = secIdLower.includes("contact") || secIdLower.includes("identity") || secIdLower.includes("who_submitted") || secTitleLower.includes("who submitted") || secTitleLower.includes("your details");
                  const isTimingSection = secIdLower.includes("why_now") || secIdLower.includes("urgency") || secIdLower.includes("timing") || secTitleLower.includes("why now");
                  const isProfileSection = secIdLower.includes("company_profile") || secIdLower.includes("headcount") || secTitleLower.includes("company profile");
                  const isEngagementSection = secIdLower.includes("engagement") || secTitleLower.includes("engagement");
                  const isTargetsSection = secIdLower.includes("target") || secIdLower.includes("asset") || secTitleLower.includes("target");
                  const isAuthSection = secIdLower.includes("auth") || secIdLower.includes("authorisation") || secTitleLower.includes("authorization");

                  // Questions helper renderer
                  const renderQuestionsList = (questions: any[], filterQids: string[] = []) => {
                    const filteredQuestions = (questions || []).filter(
                      (q: any) => !filterQids.includes(String(q.qid || q.id || "").toLowerCase())
                    );
                    if (filteredQuestions.length === 0) return null;

                    return (
                      <div className="space-y-3 text-xs pt-2">
                        {filteredQuestions.map((q: any) => {
                          const qKey = q.qid || q.id || q.key;
                          const ansVal = answersObj[qKey] ?? answersObj[q.label];
                          let finalAns = ansVal !== undefined && ansVal !== null ? formatReactChild(ansVal) : "Not provided";
                          let matchedFlags: string[] = [];

                          if (ansVal !== undefined && ansVal !== null) {
                            const rawVal = typeof ansVal === "object" && ansVal !== null && "rawValue" in ansVal ? ansVal.rawValue : ansVal;
                            if (typeof ansVal === "object" && ansVal !== null && Array.isArray(ansVal.flags)) {
                              matchedFlags = ansVal.flags;
                            }
                            if (q.type === "single_choice") {
                              const opt = (q.options || []).find((o: any) => o.value === rawVal || o.label === rawVal);
                              if (opt) {
                                finalAns = formatReactChild(opt.label);
                                if (matchedFlags.length === 0) matchedFlags = opt.flags || [];
                              }
                            } else if (q.type === "multi_choice" && Array.isArray(rawVal)) {
                              finalAns = rawVal.map((v) => {
                                const opt = (q.options || []).find((o: any) => o.value === v || o.label === v);
                                return opt ? formatReactChild(opt.label) : formatReactChild(v);
                              }).join(", ");
                            }
                          }

                          return (
                            <div key={qKey || q.label} className="space-y-1">
                              <div className="flex items-center gap-1.5">
                                <span className="text-slate-400 font-medium">{formatReactChild(q.label || q.title || qKey)}</span>
                                {showFieldKeys && <span className="font-mono text-[9px] text-slate-400">({qKey})</span>}
                              </div>
                              <div className="font-bold text-slate-900 leading-relaxed whitespace-pre-line">
                                {finalAns}
                              </div>
                              {matchedFlags.length > 0 && (
                                <div className="flex flex-wrap gap-1 pt-0.5">
                                  {matchedFlags.map((f, fi) => (
                                    <span key={fi} className="text-[9px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-100 px-1.5 py-0.5 rounded">
                                      {formatReactChild(f)}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  };

                  // 1. Contact / Identity Section
                  if (isContactSection) {
                    return (
                      <div key={sec.id || secIdx} className="border-b border-slate-100 pb-5 space-y-3 last:border-0 last:pb-0">
                        <div>
                          <h3 className="text-sm font-bold text-slate-900 inline-block mr-2">{sec.title || "Who submitted this"}</h3>
                          <span className="text-xs text-slate-400">{sec.description || "Contact and company identity from the questionnaire."}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-xs pt-1">
                          <div>
                            <div className="flex items-center gap-1">
                              <span className="text-slate-400 font-medium">Company name</span>
                              {showFieldKeys && <span className="font-mono text-[9px] text-slate-400">q_company</span>}
                            </div>
                            <div className="font-bold text-slate-900 mt-0.5">{clientName}</div>
                          </div>
                          <div>
                            <div className="flex items-center gap-1">
                              <span className="text-slate-400 font-medium">Your name</span>
                              {showFieldKeys && <span className="font-mono text-[9px] text-slate-400">q_name</span>}
                            </div>
                            <div className="font-bold text-slate-900 mt-0.5">{submitterName}</div>
                          </div>
                          <div>
                            <div className="flex items-center gap-1">
                              <span className="text-slate-400 font-medium">Contact email</span>
                              {showFieldKeys && <span className="font-mono text-[9px] text-slate-400">q_email</span>}
                            </div>
                            <div className="font-bold text-slate-900 mt-0.5">{clientEmail}</div>
                          </div>
                          <div>
                            <div className="flex items-center gap-1">
                              <span className="text-slate-400 font-medium">Your role</span>
                              {showFieldKeys && <span className="font-mono text-[9px] text-slate-400">q_role</span>}
                            </div>
                            <div className="font-bold text-slate-900 mt-0.5">{clientRole}</div>
                          </div>
                        </div>
                        {renderQuestionsList(sec.questions, [
                          "client_name",
                          "client_contact",
                          "client_email",
                          "client_role",
                          "q_company",
                          "q_name",
                          "q_email",
                          "q_role",
                          "company_name",
                          "contact_name",
                          "contact_email",
                          "contact_role",
                          "company",
                          "name",
                          "email",
                          "role"
                        ])}
                      </div>
                    );
                  }

                  // 2. Timing / Urgency Section
                  if (isTimingSection) {
                    return (
                      <div key={sec.id || secIdx} className="border-b border-slate-100 pb-5 space-y-2 last:border-0 last:pb-0">
                        <div>
                          <h3 className="text-sm font-bold text-slate-900 inline-block mr-2">{sec.title || "Why now"}</h3>
                          <span className="text-xs text-slate-400">{sec.description || "Timing pressure driving the request."}</span>
                        </div>
                        <div className="text-xs pt-1 space-y-1.5">
                          <div className="flex items-center gap-1">
                            <span className="text-slate-400 font-medium">How soon do you need testing complete?</span>
                            {showFieldKeys && <span className="font-mono text-[9px] text-slate-400">q_urgency</span>}
                          </div>
                          <div className="font-bold text-slate-900">{urgencyWindow}</div>
                          <div>
                            <span className="inline-block px-2.5 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-md text-[11px] font-semibold">
                              Fast turnaround
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-slate-400 pt-1">
                            <Info size={13} className="text-slate-400 shrink-0" />
                            <span>Tight window — confirm tester availability before quoting.</span>
                          </div>
                        </div>
                        {renderQuestionsList(sec.questions, ["q_urgency", "urgency_window", "urgency", "timeframe"])}
                      </div>
                    );
                  }

                  // 3. Company Profile Section
                  if (isProfileSection) {
                    return (
                      <div key={sec.id || secIdx} className="border-b border-slate-100 pb-5 space-y-2 last:border-0 last:pb-0">
                        <div>
                          <h3 className="text-sm font-bold text-slate-900 inline-block mr-2">{sec.title || "Company profile"}</h3>
                          <span className="text-xs text-slate-400">{sec.description || "Signals used to place this lead in a tier."}</span>
                        </div>
                        <div className="text-xs pt-1 space-y-1.5">
                          <div className="flex items-center gap-1">
                            <span className="text-slate-400 font-medium">Approximate company size (headcount)</span>
                            {showFieldKeys && <span className="font-mono text-[9px] text-slate-400">q_headcount</span>}
                          </div>
                          <div className="font-bold text-slate-900">{headcountBand}</div>
                          <div className="flex items-center gap-2">
                            <span className="inline-block px-2.5 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-md text-[11px] font-semibold">
                              {internalSummary.tier || "Growing"}
                            </span>
                            <span className="inline-block px-2.5 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-md text-[11px] font-semibold">
                              Narrow scope recommended
                            </span>
                          </div>
                        </div>
                        {renderQuestionsList(sec.questions, ["q_headcount", "headcount_band", "company_size", "headcount"])}
                      </div>
                    );
                  }

                  // 4. Engagement Preference Section
                  if (isEngagementSection) {
                    return (
                      <div key={sec.id || secIdx} className="border-b border-slate-100 pb-5 space-y-2 last:border-0 last:pb-0">
                        <div>
                          <h3 className="text-sm font-bold text-slate-900 inline-block mr-2">{sec.title || "Engagement preference"}</h3>
                          <span className="text-xs text-slate-400">{sec.description || "How the client wants to buy."}</span>
                        </div>
                        <div className="text-xs pt-1 space-y-1.5">
                          <div className="flex items-center gap-1">
                            <span className="text-slate-400 font-medium">One-time or continuous?</span>
                            {showFieldKeys && <span className="font-mono text-[9px] text-slate-400">q_engagement</span>}
                          </div>
                          <div className="font-bold text-slate-900">{engagementPref}</div>
                          <div className="flex items-center gap-1.5 text-xs text-slate-400 pt-0.5">
                            <Info size={13} className="text-slate-400 shrink-0" />
                            <span>Continuous testing is an upsell path, not a stated need.</span>
                          </div>
                        </div>
                        {renderQuestionsList(sec.questions, ["q_engagement", "engagement_preference", "engagement_model", "testing_type"])}
                      </div>
                    );
                  }

                  // 5. Targets Section
                  if (isTargetsSection) {
                    return (
                      <div key={sec.id || secIdx} className="border-b border-slate-100 pb-5 space-y-3 last:border-0 last:pb-0">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-sm font-bold text-slate-900 inline-block mr-2">{sec.title || "Targets in scope"}</h3>
                            <span className="text-xs text-slate-400">{sec.description || `${totalTargetCount} asset${totalTargetCount !== 1 ? 's' : ''} nominated for testing.`}</span>
                          </div>
                          <button
                            onClick={() => {
                              setTargetModalCategory("ALL");
                              setTargetModalSearch("");
                              setTargetModalEnv("ALL");
                              setTargetModalAccess("ALL");
                              setShowTargetsModal(true);
                            }}
                            className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition"
                          >
                            View all targets
                          </button>
                        </div>

                        {/* Dynamic Target Summary Table */}
                        <div className="border border-slate-200 rounded-xl overflow-hidden text-xs">
                          <table className="w-full text-left">
                            <thead className="bg-slate-50 border-b border-slate-200 text-slate-400 font-medium text-[11px]">
                              <tr>
                                <th className="py-2.5 px-4 font-medium">Target type</th>
                                <th className="py-2.5 px-4 font-medium text-center">Targets</th>
                                <th className="py-2.5 px-4 font-medium text-right">Access readiness</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 text-slate-700">
                              {Object.entries(targetTypeCounts).map(([key, item]) => (
                                <tr key={key} className="hover:bg-slate-50/50 transition">
                                  <td className="py-3 px-4 font-semibold text-slate-900 flex items-center gap-2">
                                    <span className="text-slate-500">{item.icon}</span>
                                    <span>{item.label}</span>
                                  </td>
                                  <td className="py-3 px-4 font-bold text-slate-900 text-center">{item.count}</td>
                                  <td className="py-3 px-4 text-slate-500 font-medium text-right">
                                    <button
                                      onClick={() => {
                                        setTargetModalCategory(item.label);
                                        setTargetModalSearch("");
                                        setTargetModalEnv("ALL");
                                        setTargetModalAccess("ALL");
                                        setShowTargetsModal(true);
                                      }}
                                      className="hover:text-indigo-600 transition inline-flex items-center gap-1 font-semibold"
                                    >
                                      <span>
                                        {yesAccessCount > 0 || noAccessCount > 0
                                          ? `${yesAccessCount} creds required${noAccessCount > 0 ? ` · ${noAccessCount} no creds` : ''}`
                                          : `${item.count} configured`}
                                      </span>
                                      <ChevronRight size={13} />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Nominated Targets Quick Cards */}
                        {parsedTargets.length > 0 && (
                          <div className="pt-2 space-y-2">
                            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                              Nominated Assets ({parsedTargets.length}):
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                              {parsedTargets.map((t) => (
                                <div
                                  key={t.id}
                                  onClick={() => {
                                    setTargetModalCategory("ALL");
                                    setTargetModalSearch("");
                                    setTargetModalEnv("ALL");
                                    setTargetModalAccess("ALL");
                                    setExpandedTargetIds({ [t.id]: true });
                                    setShowTargetsModal(true);
                                  }}
                                  className="group p-3 rounded-xl border border-slate-200/90 bg-white hover:border-indigo-300 hover:shadow-sm cursor-pointer transition flex items-start justify-between gap-2"
                                >
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5 font-bold text-xs text-slate-900 group-hover:text-indigo-600 transition">
                                      <span>{t.icon}</span>
                                      <span className="truncate">{formatReactChild(t.name)}</span>
                                    </div>
                                    <div className="text-[11px] font-mono text-slate-400 truncate mt-0.5">
                                      {formatReactChild(t.address)}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                                      <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-700">
                                        {formatReactChild(t.typeLabel)}
                                      </span>
                                      {t.details?.__classification?.label && (
                                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#0B132B] text-white">
                                          {t.details.__classification.label}
                                        </span>
                                      )}
                                      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold text-slate-600 bg-slate-50 border border-slate-200">
                                        {formatReactChild(t.size)}
                                      </span>
                                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                        t.accessStatus === "Yes"
                                          ? "bg-amber-50 text-amber-800 border border-amber-200/60"
                                          : t.accessStatus === "No"
                                          ? "bg-emerald-50 text-emerald-800 border border-emerald-200/60"
                                          : "bg-slate-100 text-slate-600"
                                      }`}>
                                        Auth: {t.accessStatus}
                                      </span>
                                    </div>
                                  </div>
                                  <ChevronRight size={14} className="text-slate-400 group-hover:text-indigo-600 group-hover:translate-x-0.5 transition shrink-0 mt-1" />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {renderQuestionsList(sec.questions, ["business_assets", "target_builder"])}
                      </div>
                    );
                  }

                  // 6. Authorisation Section
                  if (isAuthSection) {
                    return (
                      <div key={sec.id || secIdx} className="border-b border-slate-100 pb-5 space-y-2 last:border-0 last:pb-0">
                        <div>
                          <h3 className="text-sm font-bold text-slate-900 inline-block mr-2">{sec.title || "Authorisation"}</h3>
                          <span className="text-xs text-slate-400">{sec.description || "Signed off by the submitting contact."}</span>
                        </div>
                        {renderQuestionsList(sec.questions, ["authorisation", "authority"])}
                      </div>
                    );
                  }

                  // 7. General Custom Questionnaire Section
                  return (
                    <div key={sec.id || secIdx} className="border-b border-slate-100 pb-5 space-y-3 last:border-0 last:pb-0">
                      <div>
                        <h3 className="text-sm font-bold text-slate-900 inline-block mr-2">{sec.title || (sec.id ? sec.id.replace(/_/g, " ") : "Section")}</h3>
                        {sec.description && <span className="text-xs text-slate-400">{sec.description}</span>}
                      </div>

                      {renderQuestionsList(sec.questions)}
                    </div>
                  );
                })}

              </div>
            )}

            {/* TAB 3: Sizing breakdown */}
            {activeTab === "sizing" && (
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden text-xs">
                <div className="p-6 border-b border-slate-100">
                  <h3 className="text-base font-bold text-slate-900">Sizing breakdown</h3>
                  <p className="text-xs text-slate-400 font-normal mt-0.5">
                    How the engine turned the submitted scope into days and money.
                  </p>
                </div>

                <div className="w-full">
                  <table className="w-full text-left">
                    <thead className="bg-white border-b border-slate-100 text-slate-400 font-medium text-[11px]">
                      <tr>
                        <th className="py-3 px-6 font-medium">Line item</th>
                        <th className="py-3 px-6 font-medium text-center">Days</th>
                        <th className="py-3 px-6 font-medium text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-slate-700">
                      {/* Core testing scope line item */}
                      <tr className="hover:bg-slate-50/50 transition">
                        <td className="py-4 px-6">
                          <div className="font-bold text-slate-900 text-sm">Core testing scope</div>
                          <div className="text-xs text-slate-400 mt-0.5">
                            {parsedTargets.length > 0
                              ? parsedTargets.map(t => `${t.typeLabel} (${t.size || "Standard"})`).slice(0, 2).join(" · ") + (parsedTargets.length > 2 ? ` + ${parsedTargets.length - 2} more` : '')
                              : "Web application pentest · Standard, unauthenticated"}
                          </div>
                        </td>
                        <td className="py-4 px-6 font-medium text-slate-800 text-center text-sm">
                          {currentPriceObj.testingDays || 5}
                        </td>
                        <td className="py-4 px-6 font-bold text-slate-900 text-right text-sm">
                          {activeCurrency} ${((currentPriceObj.testingDays || 5) * (REGION_DAILY_RATES[region]?.rate || 2000)).toLocaleString()}
                        </td>
                      </tr>

                      {/* Reporting & overhead line item */}
                      <tr className="hover:bg-slate-50/50 transition">
                        <td className="py-4 px-6">
                          <div className="font-bold text-slate-900 text-sm">Reporting & overhead</div>
                          <div className="text-xs text-slate-400 mt-0.5">
                            Findings write-up, remediation guidance, readout call
                          </div>
                        </td>
                        <td className="py-4 px-6 font-medium text-slate-800 text-center text-sm">
                          {currentPriceObj.reportingDays || 1}
                        </td>
                        <td className="py-4 px-6 font-bold text-slate-900 text-right text-sm">
                          {activeCurrency} ${((currentPriceObj.reportingDays || 1) * (REGION_DAILY_RATES[region]?.rate || 2000)).toLocaleString()}
                        </td>
                      </tr>

                      {/* Total scope window line item */}
                      <tr className="bg-slate-50/40 font-bold text-slate-900">
                        <td className="py-4 px-6">
                          <div className="font-bold text-slate-900 text-sm">Total scope window</div>
                          <div className="text-xs text-slate-400 font-normal mt-0.5">
                            {activeCurrency} ${(REGION_DAILY_RATES[region]?.rate || 2000).toLocaleString()} per tester day
                          </div>
                        </td>
                        <td className="py-4 px-6 font-bold text-slate-900 text-center text-sm">
                          {(currentPriceObj.testingDays || 5) + (currentPriceObj.reportingDays || 1)}
                        </td>
                        <td className="py-4 px-6 font-bold text-slate-900 text-right text-base">
                          {activeCurrency} ${finalPrice.toLocaleString()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          </div>

          {/* ── RIGHT COLUMN (Sidebar Panels - Exact Match to Design Screenshots) ── */}
          <div className="space-y-6">

            {/* Panel 1: Pricing Recalculator */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-4">
              <div>
                <h3 className="text-sm font-bold text-slate-900">Pricing recalculator</h3>
                <p className="text-xs text-slate-400 font-normal mt-0.5">Adjust region, modifiers, or override the number entirely.</p>
              </div>

              {/* Region Rate Card Segmented Selector */}
              <div className="space-y-1.5 pt-1">
                <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">Region rate card</label>
                <div className="grid grid-cols-4 gap-1 bg-slate-100 p-1 rounded-lg border border-slate-200 text-xs font-semibold">
                  {["NZ", "AU", "US", "SG"].map(reg => (
                    <button
                      key={reg}
                      type="button"
                      onClick={() => setRegion(reg)}
                      className={`py-1.5 rounded-md text-xs font-bold transition ${
                        region === reg
                          ? "bg-[#0B132B] text-white shadow-sm"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      {reg}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-slate-400 pt-0.5">
                  {region === "NZ" ? "New Zealand" : region === "AU" ? "Australia" : region === "US" ? "United States" : "Singapore"} · {activeCurrency} ${REGION_DAILY_RATES[region]?.rate || 2000} per tester day
                </p>
              </div>

              {/* Checkbox Toggles */}
              <div className="space-y-3 pt-3 border-t border-slate-100 text-xs">
                <label className="flex items-start gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={competitiveMode}
                    onChange={(e) => setCompetitiveMode(e.target.checked)}
                    className="mt-0.5 rounded border-slate-300 text-slate-900 focus:ring-slate-900 w-4 h-4"
                  />
                  <div>
                    <span className="font-bold text-slate-800 block">Competitive mode</span>
                    <span className="text-slate-400 text-[11px] block">Applies a 12% discount</span>
                  </div>
                </label>

                <label className="flex items-start gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={continuousACV}
                    onChange={(e) => setContinuousACV(e.target.checked)}
                    className="mt-0.5 rounded border-slate-300 text-slate-900 focus:ring-slate-900 w-4 h-4"
                  />
                  <div>
                    <span className="font-bold text-slate-800 block">Continuous testing</span>
                    <span className="text-slate-400 text-[11px] block">Prices as annual contract value (+18%)</span>
                  </div>
                </label>
              </div>

              {/* Sales Modifier Slider */}
              <div className="space-y-1.5 pt-3 border-t border-slate-100 text-xs">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-slate-800">Sales modifier</span>
                  <span className="font-bold text-slate-900 text-xs">{manualCoefficient.toFixed(2)}×</span>
                </div>
                <input
                  type="range"
                  min="0.85"
                  max="1.15"
                  step="0.01"
                  value={manualCoefficient}
                  onChange={(e) => setManualCoefficient(Number(e.target.value))}
                  className="w-full accent-slate-900 cursor-pointer h-1.5 bg-slate-100 rounded-lg appearance-none"
                />
                <div className="flex justify-between text-[10px] text-slate-400 font-medium">
                  <span>0.85×</span>
                  <span>1.15×</span>
                </div>
              </div>

              {/* Manual Override Input */}
              <div className="space-y-1.5 pt-3 border-t border-slate-100 text-xs">
                <label className="font-bold text-slate-800 block">Manual override</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-xs text-slate-400 font-bold">{activeCurrency}</span>
                  <input
                    type="number"
                    min="0"
                    placeholder="Leave blank to use the calculator"
                    value={manualOverridePrice}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "") setManualOverridePrice("");
                      else {
                        const num = Number(val);
                        if (num >= 0) setManualOverridePrice(num);
                      }
                    }}
                    className="w-full pl-12 pr-3 py-2 border border-slate-200 rounded-lg text-xs font-semibold text-slate-800 bg-white focus:outline-none focus:border-slate-900 transition"
                  />
                </div>
              </div>

              {/* Action Button & Current Quote Footer */}
              <div className="pt-2 space-y-2">
                <button
                  type="button"
                  onClick={handleApplyPricingOverrides}
                  className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold text-xs rounded-lg transition text-center shadow-sm"
                >
                  Quote is up to date
                </button>
                <div className="text-center text-xs text-slate-400 font-medium">
                  Current quote <strong className="text-slate-800 font-bold">{activeCurrency} ${finalPrice.toLocaleString()}</strong>
                </div>
              </div>

            </div>

            {/* Panel 2: Quick Details Card */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-4">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Quick details</h3>

              <div className="space-y-3">
                
                {/* Item 1: Proposal Playbook */}
                <div
                  onClick={() => setShowPlaybookDrawer(true)}
                  className="p-3.5 border border-slate-200 rounded-xl hover:border-slate-300 transition cursor-pointer flex items-center justify-between gap-3 bg-slate-50/50"
                >
                  <div className="flex items-start gap-3 min-w-0">
                    <span className="text-base leading-none">📄</span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-bold text-slate-900 text-xs truncate">Proposal playbook</h4>
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-100">
                          {attachedCount} attached
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        3 required · 1 recommended · 2 optional
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-slate-500 font-semibold shrink-0">
                    <span>6 assets</span>
                    <ChevronRight size={14} />
                  </div>
                </div>

                {/* Item 2: Access & NDA */}
                <div
                  onClick={() => setShowAccessModal(true)}
                  className="p-3.5 border border-slate-200 rounded-xl hover:border-slate-300 transition cursor-pointer flex items-center justify-between gap-3 bg-slate-50/50"
                >
                  <div className="flex items-start gap-3 min-w-0">
                    <span className="text-base leading-none">👥</span>
                    <div className="min-w-0">
                      <h4 className="font-bold text-slate-900 text-xs">Access & NDA</h4>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        {data.nda_status === "SIGNED" ? "Signed NDA (PDF Available)" : "NDA not required"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-slate-500 font-semibold shrink-0">
                    <span>{allUsersWithAccess.length || 2} users</span>
                    <ChevronRight size={14} />
                  </div>
                </div>

              </div>
            </div>

          </div>

        </div>
      </div>

      {/* ─── SLIDE-OVER DRAWER: Proposal Playbook (Exact Match to Design Screenshot 3) ─── */}
      {showPlaybookDrawer && ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop Overlay */}
          <div
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs transition-opacity"
            onClick={() => setShowPlaybookDrawer(false)}
          />

          <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
            <div className="w-screen max-w-md bg-white shadow-2xl flex flex-col justify-between border-l border-slate-200 animate-in slide-in-from-right duration-200">
              
              {/* Drawer Header */}
              <div className="p-6 border-b border-slate-100 flex items-start justify-between">
                <div>
                  <h3 className="text-base font-bold text-slate-900">Proposal playbook</h3>
                  <p className="text-xs text-slate-400 font-normal mt-0.5">
                    {attachedCount} of {DEFAULT_PLAYBOOK_ITEMS.length} assets attached to the send.
                  </p>
                </div>
                <button
                  onClick={() => setShowPlaybookDrawer(false)}
                  className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Drawer Content Body */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6 text-xs">

                {/* Section 1: Must Include */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-bold text-slate-900 text-xs">Must include</h4>
                    <span className="text-[11px] text-slate-400 font-medium">Locked by playbook rules</span>
                  </div>

                  <div className="space-y-2.5">
                    {DEFAULT_PLAYBOOK_ITEMS.filter((item) => item.category === "must").map((item) => (
                      <div
                        key={item.id}
                        className="p-3.5 border border-slate-200 rounded-xl bg-slate-50/50 space-y-1.5 relative"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2 font-bold text-slate-900">
                            <Lock size={13} className="text-slate-400 shrink-0" />
                            <span>{item.title}</span>
                          </div>
                          <span className="font-mono text-[9px] font-bold text-slate-400 uppercase bg-white border border-slate-200 px-1.5 py-0.5 rounded">
                            {item.code}
                          </span>
                        </div>
                        <p className="text-slate-500 text-[11px] leading-relaxed">{item.description}</p>
                        <p className="text-[10px] text-slate-400 pt-0.5">{item.meta}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Section 2: Strongly Recommended */}
                <div className="space-y-3 pt-2 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <h4 className="font-bold text-slate-900 text-xs">Strongly recommended</h4>
                    <span className="text-[11px] text-indigo-600 font-medium">Matched on route</span>
                  </div>

                  <div className="space-y-2.5">
                    {DEFAULT_PLAYBOOK_ITEMS.filter((item) => item.category === "strong").map((item) => {
                      const isChecked = selectedPlaybookItemIds.includes(item.id);
                      return (
                        <div
                          key={item.id}
                          onClick={() => togglePlaybookItem(item.id)}
                          className={`p-3.5 border rounded-xl transition cursor-pointer space-y-1.5 ${
                            isChecked ? "bg-indigo-50/40 border-indigo-200" : "bg-white border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2 font-bold text-slate-900">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => {}}
                                className="rounded border-slate-300 text-slate-900 focus:ring-slate-900 w-4 h-4 cursor-pointer"
                              />
                              <span>{item.title}</span>
                            </div>
                            <span className="font-mono text-[9px] font-bold text-slate-400 uppercase bg-white border border-slate-200 px-1.5 py-0.5 rounded">
                              {item.code}
                            </span>
                          </div>
                          <p className="text-slate-500 text-[11px] leading-relaxed pl-6">{item.description}</p>
                          <p className="text-[10px] text-slate-400 pl-6 pt-0.5">{item.meta}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Section 3: Optional */}
                <div className="space-y-3 pt-2 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <h4 className="font-bold text-slate-900 text-xs">Optional</h4>
                    <span className="text-[11px] text-slate-400 font-medium">Add if the call goes well</span>
                  </div>

                  <div className="space-y-2.5">
                    {DEFAULT_PLAYBOOK_ITEMS.filter((item) => item.category === "optional").map((item) => {
                      const isChecked = selectedPlaybookItemIds.includes(item.id);
                      return (
                        <div
                          key={item.id}
                          onClick={() => togglePlaybookItem(item.id)}
                          className={`p-3.5 border rounded-xl transition cursor-pointer space-y-1.5 ${
                            isChecked ? "bg-indigo-50/40 border-indigo-200" : "bg-white border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2 font-bold text-slate-900">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => {}}
                                className="rounded border-slate-300 text-slate-900 focus:ring-slate-900 w-4 h-4 cursor-pointer"
                              />
                              <span>{item.title}</span>
                            </div>
                            <span className="font-mono text-[9px] font-bold text-slate-400 uppercase bg-white border border-slate-200 px-1.5 py-0.5 rounded">
                              {item.code}
                            </span>
                          </div>
                          <p className="text-slate-500 text-[11px] leading-relaxed pl-6">{item.description}</p>
                          <p className="text-[10px] text-slate-400 pl-6 pt-0.5">{item.meta}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>

              </div>

              {/* Drawer Footer Fixed Action Button */}
              <div className="p-4 bg-slate-50 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => {
                    setShowPlaybookDrawer(false);
                    navigate(`/dashboard/sales/scoping/${id}/sow`);
                  }}
                  className="w-full py-3 bg-[#0B132B] hover:bg-slate-800 text-white rounded-xl text-xs font-bold shadow-lg transition flex items-center justify-center gap-2"
                >
                  <span>Send proposal ·</span>
                </button>
              </div>

            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ─── SLIDE-OVER DRAWER: Access & NDA Quick Detail Drawer (Matches Proposal Playbook Drawer) ─── */}
      {showAccessModal && ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop Overlay */}
          <div
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs transition-opacity"
            onClick={() => setShowAccessModal(false)}
          />

          <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
            <div className="w-screen max-w-md bg-white shadow-2xl flex flex-col justify-between border-l border-slate-200 animate-in slide-in-from-right duration-200">
              
              {/* Drawer Header */}
              <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                    <span>👥</span> Access & NDA
                  </h3>
                  <p className="text-xs text-slate-400 font-normal mt-0.5">
                    Manage engagement access permissions & NDA settings.
                  </p>
                </div>
                <button
                  onClick={() => setShowAccessModal(false)}
                  className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Drawer Body Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6 text-xs">
                {/* NDA Toggle Card */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between">
                  <div>
                    <span className="font-bold text-slate-900 block text-xs">Require NDA Signature</span>
                    <span className="text-slate-400 text-[11px]">Force client signature before questionnaire access</span>
                  </div>
                  <button
                    type="button"
                    disabled={isTogglingNda}
                    onClick={handleToggleNda}
                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 transition-colors ${
                      data.nda_required ? "bg-slate-900 border-transparent" : "bg-slate-200 border-slate-400"
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${
                        data.nda_required ? "translate-x-4" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>

                {/* Users with Access Section */}
                <div className="space-y-3 pt-2 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-900 text-xs">Users with view access</span>
                    <span className="text-[11px] text-slate-400 font-medium">{allUsersWithAccess.length} active</span>
                  </div>
                  <div className="space-y-2">
                    {allUsersWithAccess.map((u) => (
                      <div key={u.user_id} className="p-3 bg-white border border-slate-200 rounded-xl flex items-center justify-between shadow-xs">
                        <div>
                          <div className="font-bold text-slate-900">{u.display_name || u.username}</div>
                          <div className="text-[11px] text-slate-400">{u.isCreator ? "Creator" : u.isClient ? "Client" : roleLabel(u.role)}</div>
                        </div>
                        {u.isDelegate && (
                          <button
                            onClick={() => {
                              const updated = currentDelegateIds.filter(uid => uid !== u.user_id);
                              handleUpdateAccess(updated, "revoke", u.display_name || u.username);
                            }}
                            className="text-slate-400 hover:text-rose-600 p-1 rounded-md hover:bg-rose-50 transition"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Grant Access Search */}
                <div className="space-y-2.5 pt-2 border-t border-slate-100">
                  <span className="font-bold text-slate-900 text-xs block">Grant access to CTB user</span>
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search user by name or email..."
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-slate-900 outline-none transition"
                    />
                  </div>
                  {userSearchQuery && (
                    <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-xl bg-white divide-y divide-slate-100 shadow-lg">
                      {filteredRemainingUsers.length === 0 ? (
                        <div className="p-3 text-slate-400 text-center text-xs">No matching users found</div>
                      ) : (
                        filteredRemainingUsers.map((u) => (
                          <button
                            key={u.user_id}
                            onClick={() => {
                              handleUpdateAccess([...currentDelegateIds, u.user_id], "grant", u.display_name || u.username);
                              setUserSearchQuery("");
                            }}
                            className="w-full text-left p-2.5 hover:bg-slate-50 text-slate-800 text-xs font-semibold flex justify-between items-center transition"
                          >
                            <span>{u.display_name || u.username} ({roleLabel(u.role)})</span>
                            <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">Grant</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Drawer Footer */}
              <div className="p-4 bg-slate-50 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAccessModal(false)}
                  className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition"
                >
                  Done
                </button>
              </div>

            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ─── MODAL: Confirmation Modal for Reject ─── */}
      <ConfirmationModal
        isOpen={isConfirmRejectOpen}
        onClose={() => setIsConfirmRejectOpen(false)}
        onConfirm={handleReject}
        title="Reject Scoping Questionnaire Response"
        description="Are you sure you want to reject this scoping questionnaire response?"
        warning="This will clear active scope holds for the client, allowing you to send them a new scoping link if needed."
        confirmText="Reject Scope"
        cancelText="Cancel"
      />

      {/* ─── MODAL: Extend Expiry Portal Modal ─── */}
      {isExtendModalOpen && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) setIsExtendModalOpen(false); }}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-slate-200">
            <div className="bg-[#0B132B] text-white p-6 space-y-2">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center">
                  <Clock size={18} className="text-white" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">
                    {data?.isExpired ? "Reactivate scoping link" : "Extend scoping link"}
                  </h3>
                  <p className="text-xs text-slate-300">
                    Set a new access window for the client.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4 text-xs">
              <div>
                <label className="font-bold text-slate-700 block mb-1">Select Expiry Date</label>
                <input
                  type="date"
                  value={customExpiryDate}
                  onChange={(e) => setCustomExpiryDate(e.target.value)}
                  className="w-full p-2.5 border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:border-slate-900"
                />
              </div>

              <div>
                <span className="font-bold text-slate-500 block mb-1.5">Quick Adjust</span>
                <div className="flex gap-2">
                  {[-7, 7, 14, 30].map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => adjustExpiryDate(d)}
                      className="flex-1 py-2 rounded-lg text-xs font-bold border bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                    >
                      {d > 0 ? `+${d}` : d}d
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between bg-slate-50 p-3 rounded-xl border border-slate-200">
                <span className="font-bold text-slate-500">Status</span>
                {getExpiryDiffText()}
              </div>
            </div>

            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsExtendModalOpen(false)}
                className="px-4 py-2 text-xs font-semibold text-slate-500 hover:text-slate-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleExtendExpiry}
                disabled={isExtending || !customExpiryDate}
                className="px-5 py-2.5 text-xs font-bold text-white bg-[#0B132B] hover:bg-slate-800 rounded-xl transition shadow"
              >
                {isExtending ? "Updating..." : "Update expiry"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ─── MODAL: Mark Lost Portal Modal ─── */}
      {lostModalOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 text-slate-800">
          <div className="bg-white rounded-xl border border-slate-200 shadow-2xl max-w-md w-full overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-900">Mark Prospect Intake as Lost</h3>
              <button onClick={handleCloseLostModal} className="text-slate-400 hover:text-slate-600 text-xs font-semibold">✕</button>
            </div>

            <div className="p-5 space-y-4 text-xs">
              <div className="flex flex-col gap-1.5">
                <label className="font-bold text-slate-600">Lost Reason <span className="text-rose-500">*</span></label>
                <select
                  value={lostReason}
                  onChange={(e) => setLostReason(e.target.value)}
                  className="w-full py-2 px-3 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 bg-white"
                >
                  <option value="">Select a reason...</option>
                  <option value="Didn't fill scoping questionnaire">Didn't fill scoping questionnaire</option>
                  <option value="No response / unresponsive">No response / unresponsive</option>
                  <option value="Went with competitor">Went with competitor</option>
                  <option value="Budget / pricing">Budget / pricing</option>
                  <option value="Timing / not now">Timing / not now</option>
                  <option value="Not a fit">Not a fit</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="font-bold text-slate-600">Notes {lostReason === "Other" && <span className="text-rose-500">*</span>}</label>
                <textarea
                  value={lostNote}
                  onChange={(e) => setLostNote(e.target.value)}
                  placeholder={lostReason === "Other" ? "Please detail the reason..." : "Add details..."}
                  rows={3}
                  className="w-full p-3 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 bg-white"
                />
              </div>
            </div>

            <div className="px-5 py-3.5 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2.5">
              <button onClick={handleCloseLostModal} className="px-4 py-2 border border-slate-200 text-slate-600 text-xs font-bold rounded-lg bg-white">Cancel</button>
              <button
                onClick={handleSubmitLost}
                disabled={submittingLost || !lostReason || (lostReason === "Other" && !lostNote.trim())}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-xs font-bold rounded-lg shadow transition"
              >
                {submittingLost ? "Submitting..." : "Mark Lost"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* SS Counter-signing Modal */}
      {data && (
        <SsCountersignModal
          isOpen={isCountersignModalOpen}
          onClose={() => setIsCountersignModalOpen(false)}
          responseId={data.id}
          defaultSignerName={currentUsername || "Ankita Dhakar"}
          onSuccess={(ssName, signedAt) => {
            setIsCountersignModalOpen(false);
            toast.success("NDA Counter-signed successfully!");
            setData((prev: any) => ({
              ...prev,
              nda_status: "SIGNED",
              nda_ss_signer_name: ssName,
              nda_ss_signed_at: signedAt
            }));
          }}
        />
      )}

      {/* SoW Builder Modal */}
      {sowModalOpen && (
        <SowBuilderModal
          responseId={id!}
          sowStatus={sowStatus}
          sowVersion={sowVersion}
          sowBlob={sowBlob}
          clientName={data.client_name || "Client"}
          modules={sowModules}
          initialPrice={finalPrice}
          initialCurrency={activeCurrency as "NZD" | "USD"}
          initialConsultantDays={null}
          clientEmail={data.client_email || ""}
          clientPhone=""
          clientSignerName={data.nda_signer_name || data.client_name || ""}
          clientSignerTitle={data.answers?.client_role || ""}
          clientRegisteredCompany={data.client_name || ""}
          clientCountryOfIncorporation="New Zealand"
          clientBillingName={data.client_name || ""}
          clientBillingEmail={data.client_email || ""}
          clientBillingPhone=""
          clientBillingAddress=""
          clientTaxNumber=""
          clientEmergencyContact=""
          preferredStartDate=""
          ctbEngagementLead={currentUsername || "Ankita Dhakar"}
          ctbSignerName={currentUsername || "Ankita Dhakar"}
          ctbSignerTitle="Head of Cybersecurity / Solutions Specialist"
          onClose={() => setSowModalOpen(false)}
          onUpdate={(status, version, blob) => {
            setSowStatus(status);
            setSowVersion(version);
            setSowBlob(blob);
          }}
        />
      )}

      {/* Targets Modal */}
      {showTargetsModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col overflow-hidden border border-slate-200 animate-in fade-in zoom-in-95 duration-150">
            
            {/* Modal Header */}
            <div className="px-6 pt-6 pb-4 flex items-start justify-between border-b border-slate-100">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Targets & Scope Details</h2>
                <p className="text-xs text-slate-400 mt-0.5 font-medium">
                  Showing {filteredModalTargets.length} of {parsedTargets.length} nominated asset{parsedTargets.length !== 1 ? "s" : ""} {targetModalCategory !== "ALL" ? `in ${formatReactChild(targetModalCategory)}` : ""}.
                </p>
              </div>
              <button
                onClick={() => setShowTargetsModal(false)}
                className="text-slate-400 hover:text-slate-700 p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Category Filter Pills Row */}
            <div className="px-6 py-3 flex items-center gap-2 overflow-x-auto border-b border-slate-100 text-xs shrink-0">
              <button
                type="button"
                onClick={() => setTargetModalCategory("ALL")}
                className={`px-3.5 py-1.5 rounded-full font-bold transition shrink-0 ${
                  targetModalCategory === "ALL"
                    ? "bg-[#0B132B] text-white shadow-sm"
                    : "bg-slate-100 hover:bg-slate-200 text-slate-600"
                }`}
              >
                All targets {parsedTargets.length}
              </button>
              {Object.entries(targetCategories).map(([catName, count]) => (
                <button
                  key={catName}
                  type="button"
                  onClick={() => setTargetModalCategory(catName)}
                  className={`px-3.5 py-1.5 rounded-full font-bold transition shrink-0 ${
                    targetModalCategory === catName
                      ? "bg-[#0B132B] text-white shadow-sm"
                      : "bg-slate-100 hover:bg-slate-200 text-slate-600"
                  }`}
                >
                  {formatReactChild(catName)} {count}
                </button>
              ))}
            </div>

            {/* Controls Bar: Search, Filters & Expand All */}
            <div className="px-6 py-3 flex flex-wrap items-center justify-between gap-3 bg-slate-50/50 border-b border-slate-100 shrink-0">
              <div className="flex flex-wrap items-center gap-3 flex-1">
                <div className="flex-1 min-w-[220px] relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search by name, address or owner"
                    value={targetModalSearch}
                    onChange={(e) => setTargetModalSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none transition"
                  />
                </div>
                <select
                  value={targetModalEnv}
                  onChange={(e) => setTargetModalEnv(e.target.value)}
                  className="px-3.5 py-2 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-slate-900 cursor-pointer"
                >
                  <option value="ALL">Environment</option>
                  <option value="Production">Production</option>
                  <option value="Staging">Staging</option>
                  <option value="Pre-prod">Pre-prod</option>
                  <option value="Source Repo">Source Repo</option>
                </select>
                <select
                  value={targetModalAccess}
                  onChange={(e) => setTargetModalAccess(e.target.value)}
                  className="px-3.5 py-2 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-slate-900 cursor-pointer"
                >
                  <option value="ALL">Access</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                  <option value="N/A">N/A</option>
                </select>
              </div>

              {filteredModalTargets.length > 0 && (
                <button
                  type="button"
                  onClick={allAreExpanded ? handleCollapseAll : handleExpandAll}
                  className="px-3.5 py-2 bg-white border border-slate-200 hover:border-slate-300 rounded-xl text-xs font-bold text-slate-700 flex items-center gap-1.5 transition shadow-sm shrink-0"
                >
                  {allAreExpanded ? <ChevronUp size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}
                  <span>{allAreExpanded ? "Collapse All Details" : "Expand All Details"}</span>
                </button>
              )}
            </div>

            {/* Table Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {filteredModalTargets.length === 0 ? (
                <div className="text-center py-12 text-slate-400 text-xs font-medium">
                  No targets match your current search or filter criteria.
                </div>
              ) : (
                <div className="border border-slate-200 rounded-xl overflow-hidden text-xs">
                  <table className="w-full text-left">
                    <thead className="bg-slate-50 border-b border-slate-200 text-slate-400 font-medium text-[11px]">
                      <tr>
                        <th className="py-2.5 px-4 font-medium">Target</th>
                        <th className="py-2.5 px-4 font-medium">Type</th>
                        <th className="py-2.5 px-4 font-medium">Environment</th>
                        <th className="py-2.5 px-4 font-medium">Access</th>
                        <th className="py-2.5 px-4 font-medium">Size</th>
                        <th className="py-2.5 px-4 font-medium">Owner</th>
                        <th className="py-2.5 px-4 font-medium text-right">Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-slate-700">
                      {filteredModalTargets.map((t) => {
                        const isExpanded = !!expandedTargetIds[t.id];
                        const showRaw = !!showRawDetailsIds[t.id];
                        const details = t.details || {};
                        const classification = details.__classification;

                        return (
                          <React.Fragment key={t.id}>
                            <tr
                              onClick={() => toggleTargetExpand(t.id)}
                              className={`cursor-pointer transition ${
                                isExpanded ? "bg-slate-50/90 font-medium" : "hover:bg-slate-50/50"
                              }`}
                            >
                              <td className="py-3 px-4">
                                <div className="font-bold text-slate-900">{formatReactChild(t.name)}</div>
                                <div className="text-[11px] font-mono text-slate-400 mt-0.5 break-all">{formatReactChild(t.address)}</div>
                              </td>
                              <td className="py-3 px-4 font-medium text-slate-700">
                                <div className="flex items-center gap-1.5">
                                  <span>{t.icon}</span>
                                  <span>{formatReactChild(t.typeLabel)}</span>
                                </div>
                              </td>
                              <td className="py-3 px-4 font-medium text-slate-700">
                                <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-semibold ${
                                  t.environment === "Production"
                                    ? "bg-rose-50 text-rose-800 border border-rose-200/60"
                                    : t.environment === "Staging"
                                    ? "bg-blue-50 text-blue-800 border border-blue-200/60"
                                    : "bg-slate-100 text-slate-700 border border-slate-200"
                                }`}>
                                  {formatReactChild(t.environment)}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <span className={`inline-block px-2.5 py-0.5 rounded-md font-bold text-[11px] ${
                                  t.accessStatus === "Yes"
                                    ? "bg-amber-50 text-amber-800 border border-amber-200/60"
                                    : t.accessStatus === "No"
                                    ? "bg-emerald-50 text-emerald-800 border border-emerald-200/60"
                                    : "bg-slate-100 text-slate-600 border border-slate-200"
                                }`}>
                                  {formatReactChild(t.accessStatus)}
                                </span>
                              </td>
                              <td className="py-3 px-4 font-semibold text-slate-800">
                                {formatReactChild(t.size)}
                              </td>
                              <td className="py-3 px-4 font-medium text-slate-700">
                                {formatReactChild(t.owner)}
                              </td>
                              <td className="py-3 px-4 text-right">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleTargetExpand(t.id);
                                  }}
                                  className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition inline-flex items-center gap-1.5 ${
                                    isExpanded
                                      ? "bg-[#0B132B] text-white shadow-sm"
                                      : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                                  }`}
                                >
                                  <Eye size={12} />
                                  <span>{isExpanded ? "Hide" : "Details"}</span>
                                  {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                </button>
                              </td>
                            </tr>

                            {/* Expanded Detailed View */}
                            {isExpanded && (
                              <tr className="bg-slate-50/70 border-b border-slate-200">
                                <td colSpan={7} className="p-4 sm:p-5">
                                  <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 shadow-sm space-y-4">
                                    
                                    {/* Classification & Metadata Badges */}
                                    <div className="flex flex-wrap items-center justify-between gap-2 pb-3 border-b border-slate-100">
                                      <div className="flex flex-wrap items-center gap-2">
                                        {classification?.label && (
                                          <span className="px-3 py-1 rounded-lg text-xs font-bold bg-[#0B132B] text-white flex items-center gap-1.5 shadow-sm">
                                            <Sparkles size={12} className="text-amber-400" />
                                            {classification.label}
                                          </span>
                                        )}
                                        {t.testingApproach && (
                                          <span className="px-2.5 py-1 rounded-md text-[11px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200/60">
                                            {t.testingApproach.replace(/_/g, " ")}
                                          </span>
                                        )}
                                        {t.targetKind && (
                                          <span className="px-2.5 py-1 rounded-md text-[11px] font-bold bg-slate-100 text-slate-700 border border-slate-200">
                                            {t.targetKind.replace(/_/g, " ")}
                                          </span>
                                        )}
                                        {t.exposure && (
                                          <span className="px-2.5 py-1 rounded-md text-[11px] font-bold bg-cyan-50 text-cyan-800 border border-cyan-200/60">
                                            {t.exposure.replace(/_/g, " ")}
                                          </span>
                                        )}
                                        {details.branch && (
                                          <span className="px-2.5 py-1 rounded-md text-[11px] font-mono font-medium bg-emerald-50 text-emerald-800 border border-emerald-200/60">
                                            branch: {details.branch}
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-[11px] font-mono text-slate-400">
                                        {t.targetId || t.scopeId ? `Target: ${t.targetId || t.scopeId}` : `ID: ${t.id}`}
                                      </div>
                                    </div>

                                    {/* 3-Column Structured Specs */}
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                                      
                                      {/* Column 1: Authentication & Roles */}
                                      <div className="bg-slate-50/70 rounded-xl p-3.5 border border-slate-100 space-y-2">
                                        <div className="font-bold text-slate-900 flex items-center gap-1.5">
                                          <ShieldCheck size={14} className="text-indigo-600" />
                                          <span>Authentication & Access</span>
                                        </div>
                                        <div className="space-y-1.5 text-slate-600">
                                          <div className="flex justify-between items-center">
                                            <span className="text-slate-400">Login required:</span>
                                            <span className="font-semibold text-slate-900">
                                              {details.login_required !== undefined
                                                ? details.login_required ? "Yes" : "No"
                                                : t.accessStatus}
                                            </span>
                                          </div>
                                          {details.user_roles && (
                                            <div className="flex justify-between items-center">
                                              <span className="text-slate-400">User roles:</span>
                                              <span className="font-semibold text-slate-900">
                                                {details.user_roles === "4_plus" ? "4+ roles" : details.user_roles.replace(/_/g, " ")}
                                              </span>
                                            </div>
                                          )}
                                          {Array.isArray(t.accessLevels) && t.accessLevels.length > 0 && (
                                            <div className="pt-1.5 border-t border-slate-200/60">
                                              <div className="text-slate-400 text-[10px] uppercase font-bold tracking-wider mb-1">Configured Roles:</div>
                                              <div className="flex flex-wrap gap-1">
                                                {t.accessLevels.map((lvl: string, idx: number) => (
                                                  <span key={idx} className="px-1.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-200/60 rounded text-[10px] font-semibold">
                                                    {lvl.replace(/_/g, " ")}
                                                  </span>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>

                                      {/* Column 2: Scope Metrics & Architecture */}
                                      <div className="bg-slate-50/70 rounded-xl p-3.5 border border-slate-100 space-y-2">
                                        <div className="font-bold text-slate-900 flex items-center gap-1.5">
                                          <Layers size={14} className="text-indigo-600" />
                                          <span>Scope Size & Metrics</span>
                                        </div>
                                        <div className="space-y-1.5 text-slate-600">
                                          <div className="flex justify-between items-center">
                                            <span className="text-slate-400">Calculated size:</span>
                                            <span className="font-semibold text-slate-900">{t.size}</span>
                                          </div>
                                          {details.pages_count_band && (
                                            <div className="flex justify-between items-center">
                                              <span className="text-slate-400">Pages band:</span>
                                              <span className="font-semibold text-slate-900">
                                                {details.pages_count_band === "75_plus" ? "75+ pages" : details.pages_count_band.replace(/_/g, " ")}
                                              </span>
                                            </div>
                                          )}
                                          {details.code_size_band && (
                                            <div className="flex justify-between items-center">
                                              <span className="text-slate-400">Code size band:</span>
                                              <span className="font-semibold text-slate-900">
                                                {details.code_size_band === "50k_200k" ? "50K–200K LOC" : details.code_size_band.replace(/_/g, " ")}
                                              </span>
                                            </div>
                                          )}
                                          {details.review_boundary && (
                                            <div className="flex justify-between items-center">
                                              <span className="text-slate-400">Review boundary:</span>
                                              <span className="font-semibold text-slate-900">{details.review_boundary.replace(/_/g, " ")}</span>
                                            </div>
                                          )}
                                          {Array.isArray(details.languages) && details.languages.length > 0 && (
                                            <div className="flex justify-between items-center">
                                              <span className="text-slate-400">Languages:</span>
                                              <span className="font-semibold text-slate-900 font-mono text-[11px]">{details.languages.join(", ")}</span>
                                            </div>
                                          )}
                                        </div>
                                      </div>

                                      {/* Column 3: Important Flows & Complexity Drivers */}
                                      <div className="bg-slate-50/70 rounded-xl p-3.5 border border-slate-100 space-y-2">
                                        <div className="font-bold text-slate-900 flex items-center gap-1.5">
                                          <Sliders size={14} className="text-indigo-600" />
                                          <span>Flows & Complexity Drivers</span>
                                        </div>
                                        {Array.isArray(details.important_flows) && details.important_flows.length > 0 && (
                                          <div>
                                            <div className="text-slate-400 text-[10px] uppercase font-bold tracking-wider mb-1">Key Flows:</div>
                                            <div className="flex flex-wrap gap-1">
                                              {details.important_flows.map((flow: string, fIdx: number) => (
                                                <span key={fIdx} className="px-2 py-0.5 bg-sky-50 text-sky-800 border border-sky-200/60 rounded text-[10px] font-semibold">
                                                  {flow.replace(/_/g, " ")}
                                                </span>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        {Array.isArray(classification?.complexityFlags) && classification.complexityFlags.length > 0 && (
                                          <div className="pt-1.5 border-t border-slate-200/60">
                                            <div className="text-slate-400 text-[10px] uppercase font-bold tracking-wider mb-1">Complexity Drivers:</div>
                                            <div className="flex flex-wrap gap-1">
                                              {classification.complexityFlags.map((flag: string, flIdx: number) => (
                                                <span key={flIdx} className="px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded text-[10px] font-medium">
                                                  ✓ {flag}
                                                </span>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {/* Out of Scope & Exclusions Section */}
                                    {details.excludes && (
                                      <div className="bg-amber-50/80 border border-amber-200/80 rounded-xl p-3.5 text-xs">
                                        <div className="font-bold text-amber-900 flex items-center gap-1.5 mb-1">
                                          <AlertTriangle size={14} className="text-amber-600 shrink-0" />
                                          <span>Out of Scope & Exclusions</span>
                                        </div>
                                        <p className="text-amber-900/90 leading-relaxed font-normal whitespace-pre-line">
                                          {details.excludes}
                                        </p>
                                      </div>
                                    )}

                                    {/* Scope Notes & Technical Architecture */}
                                    {details.notes && (
                                      <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3.5 text-xs">
                                        <div className="font-bold text-slate-900 flex items-center gap-1.5 mb-1.5">
                                          <FileText size={14} className="text-indigo-600 shrink-0" />
                                          <span>Scope Notes & Technical Architecture</span>
                                        </div>
                                        <p className="text-slate-700 leading-relaxed font-normal whitespace-pre-line">
                                          {details.notes}
                                        </p>
                                      </div>
                                    )}

                                    {/* Focus Areas (for Code Review or other methodologies) */}
                                    {details.focus_areas && (!details.notes || !details.notes.includes(details.focus_areas)) && (
                                      <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3.5 text-xs">
                                        <div className="font-bold text-slate-900 flex items-center gap-1.5 mb-1.5">
                                          <Target size={14} className="text-indigo-600 shrink-0" />
                                          <span>Assessment Focus Areas</span>
                                        </div>
                                        <p className="text-slate-700 leading-relaxed font-normal whitespace-pre-line">
                                          {details.focus_areas}
                                        </p>
                                      </div>
                                    )}

                                    {/* Raw Details Toggle & Inspector */}
                                    <div className="pt-2 flex items-center justify-between border-t border-slate-100 text-xs">
                                      <button
                                        type="button"
                                        onClick={() => toggleRawDetails(t.id)}
                                        className="text-indigo-600 hover:text-indigo-800 font-semibold inline-flex items-center gap-1"
                                      >
                                        <span>{showRaw ? "Hide Raw Details JSON" : "View Raw Details JSON"}</span>
                                        {showRaw ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                      </button>
                                      {showRaw && (
                                        <button
                                          type="button"
                                          onClick={() => handleCopyRawDetails(t.id, details)}
                                          className="text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 font-semibold"
                                        >
                                          {copiedTargetId === t.id ? (
                                            <>
                                              <Check size={12} className="text-emerald-600" />
                                              <span className="text-emerald-600">Copied!</span>
                                            </>
                                          ) : (
                                            <>
                                              <Copy size={12} />
                                              <span>Copy JSON</span>
                                            </>
                                          )}
                                        </button>
                                      )}
                                    </div>

                                    {showRaw && (
                                      <pre className="bg-slate-900 text-slate-100 p-3.5 rounded-xl font-mono text-[11px] overflow-x-auto max-h-60 leading-normal">
                                        {JSON.stringify(details, null, 2)}
                                      </pre>
                                    )}

                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default ScopingSessionDetails;
